/**
 * @description Bite Localization Process
 * A copy is posted in notion:
 * https://www.notion.so/Localization-L10N-Process-42b6fbda6e194a91ac50b24b295c9e33
 * 1. When you add a new string, just enter the English version and leave all other languages blank.
 *    The compile script will make sure all other languages have the new key.
 * 2. To make sure a different language won’t break the UI, try a string that’s 50% longer than the
 *    English one you are using.
 * 3. If changing a localized string, delete the other languages and leave them blank.
 * 4. Once a month, we collect all the strings in this file, and export them for our translators.
 *    The back and forth with them has some overhead both in terms of time and money, hence not
 *    doing it more frequently. Of course, if we have a big feature with lots of strings we can kick
 *    off a one-off translation export.
 */
// Use backticks instead of quotes to avoid having to escape apostrophes and quotes in the strings
/* oxlint quotes: ["error", "backtick"] */
export const Strings = {
    ACCOUNT: `Account`,
    ACCOUNT_PROFILE: `Account Profile`,
    ACCOUNT_CREATED: `Account created`,
    ACCOUNT_VERIFIED: `Account verified`,
    LOG_IN_TO_START_ORDERING: `Log in to start ordering.`,
    VERIFY_LINK_INVALID: `This verification link is no longer valid`,
    VERIFY_LINK_INVALID_LOG_IN: `The verification link has expired or has already been used. If your account is already verified, log in.`,
    ORDERS: `Orders`,
    PROFILE: `Profile`,
    PROFILE_UPDATED: `Your profile has been updated.`,
    PAYMENT_METHODS: `Payment methods`,
    PERSONAL_DETAILS: `Personal details`,
    SAVE_CHANGES: `Save changes`,
    ADD_CARD: `Add card`,
    CARD_NICKNAME: `Card nickname`,
    NO_SAVED_CARDS: `No saved cards yet.`,
    QR_CODE: `QR code`,
    REFRESH_QR_CODE: `Refresh QR code`,
    QR_CODE_SCAN_TO_PAY: `Scan at a participating kiosk to pay with {{$1: card name}}.`,
    ADA_TOGGLE_ON: `Tap to enable Reduced Screen Height`,
    ADA_TOGGLE_OFF: `Tap to disable Reduced Screen Height`,
    ADD: `Add`,
    ADD_DONATION: `Add Donation`,
    ADD_FUNDS: `Add Funds Here`,
    ADD_TIP: `Add a Tip`,
    ADD_TO_FAVORITES_WEB: ``,
    ADD_TO_ORDER: `Add to Order`,
    ADD_NEW: `Add New`,
    ADDED: `Added`,
    ADDED_TO_FAVORITES_WEB: ``,
    ADDED_TO_ORDER: `Added to Order`,
    ADDON_TEXTFIELD_PLACEHOLDER: `Type in your choice.`,
    ADDRESS_CITY: `City`,
    ADDRESS_LINE_1: `Street Address`,
    ADDRESS_LINE_2: `Apt, Floor, Suite, Building, Company (Optional)`,
    ADDRESS_POSTAL_CODE: `Postal Code`,
    ALCOHOL_VERIFICATION: `Alcohol Verification`,
    ALREADY_HAVE_AN_ACCOUNT: `Already have an account?`,
    ALSO_CONSIDER: `Also Consider...`,
    APPLIED_STORED_VALUE_CARDS: `Gift Cards`,
    ASAP: `ASAP`,
    AT_LEAST_X: `at least {{$1: number}}`,
    BACK: `Back`,
    BACK_TO_MENU: `Back to Menu`,
    BACK_TO_MENU_FROM_LOYALTY: `Back to Menu`,
    BACK_TO_CART: `Back to Cart`,
    BACK_TO_PAYMENT_OPTIONS: `Back to Payment Options`,
    BACK_TO_PAYMENT_OPTIONS_FROM_LOYALTY: `Back to Payment Options`,
    BAD_ACCESS_CODE: `Bad access code`,
    BALANCE: `Balance`,
    BAG_OPT_IN_PROMPT: `Would you like to add a To Go Bag to your order?`,
    BAG_OPT_IN_TITLE: `Add a bag?`,
    BAG_OPT_IN: `Yes, I'd Like a Bag`,
    BAG_OPT_OUT: `No, Thanks`,
    BLOCKED_WORD_WARNING: `Please remove blocked words`,
    CALORIES: `Calories`,
    CANCEL: `Cancel`,
    CANCEL_CHECKOUT: `Cancel`,
    CASH: `Cashier`,
    CARD_ENTRY_APM: `APM`,
    CARD_ENTRY_APPLE_PAY: `Apple Pay`,
    CARD_ENTRY_CARD_ON_FILE: `Card on File`,
    CARD_ENTRY_CHIP: `EMV`,
    CARD_ENTRY_CONTACTLESS: `Contactless`,
    CARD_ENTRY_GOOGLE_PAY: `Google Pay`,
    CARD_ENTRY_MANUALLY_ENTERED: `Manually Entered`,
    CARD_ENTRY_PROVIDED_EXTERNALLY: `Provided Externally`,
    CARD_ENTRY_SCAN: `Scan`,
    CARD_ENTRY_SWIPE: `Swipe`,
    CARD_ENTRY_UNKNOWN: `Unknown`,
    CARD_EXPIRED: `Your card has expired`,
    CARD_EXPIRING: `Your card is expiring soon`,
    CARD_ENDING_IN: `{{$1: card brand}} ending in {{$2: last 4 digits}}`,
    CARD_EXPIRES_ON: `Exp. {{$1: expiry date}}`,
    CARD_EXPIRES_THIS_MONTH: `Expires this month`,
    CARD_EXPIRED_LABEL: `Expired`,
    REMOVE_SAVED_CARD_DESCRIPTION: `You'll need to add it again to pay with it.`,
    CARD_RENAMED: `Card renamed`,
    REMOVE_SAVED_CARD: `Remove card`,
    REMOVE_SAVED_CARD_TITLE: `Remove card?`,
    RENAME_CARD: `Rename card`,
    SHOW_QR_CODE: `Show QR code`,
    CART: `Cart`,
    CART_LABEL: `Go to Cart with {{$1: number}} items for {{$2: price}}`,
    CART_TITLE: ``,
    CHANGE_ITEM: `Change Item`,
    CHANGE_PASSWORD: `Change Password`,
    CHANGE_PASSWORD_SUCCESS: `Your password has been updated.`,
    CHARS_LEFT: `{{$1: number}} characters remaining`,
    CHECK_AMOUNT: `Checking amount...`,
    CHECKOUT: `Checkout`,
    CHECKOUT_STEP_CART: `Cart`,
    CHECKOUT_STEP_PAYMENT_OPTIONS: `Payment`,
    CHECKOUT_STEP_PAYING: `Paying`,
    CHECKOUT_STEP_DONE: `Done`,
    CHECKOUT_WITH_FORCED_MENU: `Cannot Checkout when viewing a Menu Preview. Checkout is only available when viewing the "Currently Shown" menu.`,
    CHECK_YOUR_EMAIL: `Check your email`,
    CHOOSE_OPTION: `Choose an Option`,
    CHOOSE_TIME_SLOT: `Please choose a time slot:`,
    CLEAR_ALL: `Clear All`,
    CLEARING_SESSION: `Preparing the kiosk for the next guest...`,
    CLOSE: `Close`,
    CLOSED_SCREEN_MESSAGE: `Sorry, we are closed or not accepting mobile orders.`,
    CONFIRM: `Confirm`,
    CONFIRM_ADDRESS: `Confirm Address`,
    CONFIRM_DISCARD: `Are you sure you want to discard this item?`,
    CONFIRM_DISCARD_CHANGES: `Are you sure you want to discard your changes?`,
    CONFIRM_YOUR_ITEM: `Confirm your item`,
    CONFIRM_QUIT: `Are you sure you want to quit?`,
    CONFIRM_REFUND_AMOUNT: `Are you sure you want to refund {{$1: dollar amount}} of this transaction?`,
    CONFIRMING_YOUR_CARD: `Confirming your card...`,
    CONTINUE: `Continue`,
    CONTINUE_AS_GUEST: `Continue as Guest`,
    COULD_WE_INTEREST: `Could we interest you in...`,
    COMP_CARD: `Comp Card`,
    COMP_CARD_SCAN: `Scan Comp Card`,
    COMP_CARD_SCAN_FAILED_TITLE: `Comp Card Scan Failed`,
    COMP_CARD_SCAN_FAILED_MESSAGE: `There was an error scanning the comp card. Please try again.`,
    COMP_CARD_APPLYING_TITLE: `Applying Comp Card`,
    COMP_CARD_APPLYING_ERROR_TITLE: `Comp Card Applying Error`,
    COMP_CARD_APPLYING_ERROR_MESSAGE: `There was an error applying the comp card. Please try again.`,
    COMP_CARD_ENTERING_NUMBER_ERROR_REQUIRED: `Please enter a comp card number`,
    COMP_CARD_ENTERING_NUMBER_ERROR_INVALID: `Please enter a valid comp card number`,
    COMP_CARD_ENTERING_NUMBER_ERROR_LENGTH: `Please enter a valid comp card number`,
    COMP_CARD_ENTERING_PIN_ERROR_REQUIRED: `Please enter a comp card PIN`,
    COMP_CARD_ENTERING_PIN_ERROR_INVALID: `Please enter a valid comp card PIN`,
    COMP_CARD_ENTERING_PIN_ERROR_LENGTH: `Please enter a valid comp card PIN`,
    COMP_CARD_ENTERING_NUMBER_HINT: `Enter your comp card number. Numbers can be {{$1: number}} digits long.`,
    COMP_CARD_ENTERING_PIN_HINT: `Enter your comp card PIN. Numbers can be {{$1: length}} digits long.`,
    COMP_CARD_ERROR_REMOVING: `There was an error removing the comp card. Please try again.`,
    'bottom-bar.storm-toggle.on': `Tap to enable Storm Navigation`,
    'bottom-bar.storm-toggle.off': `Tap to disable Storm Navigation`,
    'campus-card.label': `Campus Card`,
    'campus-card.payment-line-label': `Campus card redemption from {{$1: name}} for {{$2: amount}}`,
    'campus-card.payment-option-label': `Tap to pay with a Campus Card`,
    'campus-card.scan': `Scan Your Campus Card`,
    'campus-card.scan-failed.title': `Campus Card Scan Failed`,
    'campus-card.scan-failed.message': `There was an error scanning the campus card. Please try again.`,
    'campus-card.enter-number': `Enter Your Campus Card Number`,
    'campus-card.enter-pin': `Enter Your Campus Card PIN`,
    'campus-card.applying.title': `Applying Campus Card`,
    'campus-card.applying.error.title': `Campus Card Applying Error`,
    'campus-card.applying.error.message': `There was an error applying the campus card. Please try again.`,
    'campus-card.entering-number.error.required': `Please enter a campus card number`,
    'campus-card.entering-number.error.invalid': `Please enter a valid campus card number`,
    'campus-card.entering-number.error.length': `Please enter a valid campus card number`,
    'campus-card.entering-number.hint': `Enter your campus card number. Numbers can be {{$1: number}} digits long.`,
    'campus-card.entering-pin.error.required': `Please enter a campus card PIN`,
    'campus-card.entering-pin.error.invalid': `Please enter a valid campus card PIN`,
    'campus-card.entering-pin.error.length': `Please enter a valid campus card PIN`,
    'campus-card.entering-pin.hint': `Enter your campus card PIN. Numbers can be {{$1: length}} digits long.`,
    'campus-card.error-removing': `There was an error removing the campus card. Please try again.`,
    'campus-card.no-balance.title': `No Balance Available`,
    'campus-card.no-balance.message': `There is no balance available on this campus card.`,
    'campus-card.add-another': `Add Another`,
    'campus-card.remove': `Remove Campus Card`,
    'campus-card.select-account.title': `Select an Account`,
    'campus-card.select-account.loading': `Loading Accounts...`,
    'campus-card.select-account.error.title': `Unable to Load Accounts`,
    'campus-card.select-account.error.message': `We were unable to load your accounts. Please try again.`,
    'campus-card.select-account.column.account': `Account`,
    'campus-card.select-account.column.balance': `Current Balance`,
    'campus-card.select-account.error.toast-title': `Error`,
    'campus-card.select-account.error.toast-message': `Failed to validate. Please try again.`,
    'campus-card.swipe': `Swipe your campus card to pay`,
    COUPON: `Coupon`,
    COUPON_ALREADY_USED: `Coupon code already used.`,
    COUPON_MIN_SPEND_NOT_MET: `The coupon code you entered does not meet the minimum spend requirement.`,
    COUPON_NOT_FOUND: `Coupon code not found.`,
    COUPON_NOT_ACCEPTED: `The coupon code you entered was not accepted.`,
    COUPON_APPLYING: `Applying coupon`,
    COUPON_APPLYING_ERROR_TITLE: `Coupon Applying Error`,
    COUPON_APPLYING_ERROR_MESSAGE: `There was an error applying the coupon. Please try again.`,
    COUPON_ORDER_REQUIREMENTS_NOT_MET: `The coupon code you entered does not meet the order requirements.`,
    COUPON_SCANNING_ERROR_TITLE: `Coupon Scanning Error`,
    COUPON_SCANNING_ERROR_MESSAGE: `There was an error scanning the coupon. Please try again.`,
    COUPON_ENTERING_ERROR_LENGTH: `Please enter a coupon code.`,
    COUPON_ENTERING_ERROR_INVALID: `Please enter a valid coupon code.`,
    COUPON_ERROR_REMOVING: `There was an error removing the coupon. Please try again. If the problem persists, please contact a staff member.`,
    CREATE_A_NEW_ACCOUNT: `Create a New Account`,
    CREATE_ACCOUNT: `Create Account`,
    CREATE_ACCOUNT_TERMS_LINE: `By creating an account you agree to the`,
    CREDIT_CARD: `Credit Card`,
    CREDIT_CARD_NUMBER: `Card Number`,
    CURBSIDE: `Curbside`,
    CURRENT_PASSWORD: `Current Password`,
    CUSTOM_TIP: `Custom`,
    CUSTOM_TIP_INVALID: `Please enter a valid tip amount.`,
    CUSTOM_TIP_HINT: `Enter your tip amount. Max: {{$1: number}}`,
    CUSTOM_DONATION: `Custom Donation`,
    CUSTOMER_CARDS: `Use a Saved Card`,
    CUSTOMER_ACCOUNT_BUTTON_LOG_IN: `Log In`,
    CUSTOMER_ACCOUNT_BUTTON_LOG_IN_TOP_BAR: ``,
    CUSTOMER_ACCOUNT_BUTTON_LOGGED_IN: `Your Account`,
    CUSTOMIZE: `Customize`,
    DEBIT: `Debit`,
    DEBIT_CREDIT_CARD: `Debit/Credit Card`,
    DEMO_PAY: `Demo: Pay`,
    DELIVERY: `Delivery`,
    DELIVERY_ADDRESS: `Delivery Address`,
    DELIVERY_INSTRUCTIONS: ``,
    DESELECTED_MOD_PREFIX: `Remove {{$1: mod name}}`,
    DINE_IN: `Dine In`,
    DISCLAIMER_EMAIL: `Use of email address is subject to Terms of Service.`,
    DISCLAIMER_PHONE: `Use of phone number is subject to Terms of Service.`,
    DISCLAIMER_PHONE_LINK: `TERMS OF SERVICE:`,
    DISCLAIMER_PHONE_DESCRIPTION: `FOR USE OF PHONE NUMBER`,
    DISCLAIMER_NAME: `Your name will be used to confirm your order and facilitate pickup of your order at the designated area.`,
    DISCOUNTS: `Discounts`,
    DISCOUNTS_WILL_BE_APPLIED_AT_CHECKOUT: `Discounts will be applied at checkout`,
    DISCOUNT_WILL_BE_APPLIED_AT_CHECKOUT: `Discount will be applied at checkout`,
    DONATION: `Donation`,
    DONATION_APPLYING: `Applying donation...`,
    DONATION_APPLY_ERROR_TITLE: `Donation Applying Error`,
    DONATION_APPLY_ERROR_MESSAGE: `There was an error applying your donation. Please try again.`,
    DONATION_FAILED: `Donation failed. Please try again.`,
    DONATION_ROUND_UP_TITLE: `Would you like to round up your total?`,
    DONATION_ROUND_UP_TO: `Round up to {{$1: amount}}`,
    DONATION_ROUND_UP_SUBTITLE: `Would you like to round up your total to the nearest full dollar amount?`,
    DONATION_ROUND_UP_ACCEPT: `Yes, Round Up`,
    DONATION_ROUND_UP_REJECT: `No, Thanks`,
    DONATION_REMOVE: `Remove Donation`,
    DONE: `Done`,
    DONT_INCLUDE_UTENSILS: `Don't include utensils`,
    EBT: `SNAP EBT`,
    EBT_REFUND: `Refund EBT`,
    EBT_CHOICE_DIALOG_TITLE: `Which EBT method would you like to use?`,
    EBT_CHOICE_CASH_BENEFIT: `Cash Benefit`,
    EBT_CHOICE_FOOD_STAMPS: `Food Stamp`,
    EBT_CARD_SUBTITLE: `Swipe your EBT card on the credit card machine`,
    EDIT: `Edit`,
    EDIT_ORDER: `Edit Order`,
    EDIT_TIP: `Edit Tip`,
    SAVE_ORDER: `Save Order`,
    SAVE_BUTTON: `Save`,
    EMAIL_ADDRESS: `Email Address`,
    EMAIL_RECEIPT: `Email Receipt`,
    EMAIL_REQUIRED_FOR_RECEIPT: `An email is required for your receipt.`,
    EMAIL_RECEIPT_SEND_FAILED: `Could not send receipt. Please try again.`,
    EMAIL_RECEIPT_SEND_SUCCESS: `Receipt successfully sent.`,
    ENTER_ACCESS_CODE: `Enter access code:`,
    ENTER_EMAIL_CUSTOMER_IDENTIFIER: `Enter your email`,
    ENTER_GIFT_CARD_BARCODE_FALLBACK: `Can't scan? Enter your card number.`,
    ENTER_PHONE_CONSENT: `I consent to receiving marketing materials via SMS.`,
    ENTER_PHONE_CUSTOMER_IDENTIFIER: `Enter your phone number`,
    ENTER_PHONE_CUSTOMER_IDENTIFIER_WITH_NAME: `Hey {{$1 guest name}}, please enter your phone number`,
    ENTER_PHONE_LOYALTY: `Enter your mobile phone number`,
    ENTER_PHONE_RECEIPT: `Enter your mobile phone number to receive a receipt`,
    ENTER_PHONE_TEXT_WHEN_READY: `Enter your phone number to receive a text message when your order is ready!`,
    ENTER_VEHICLE_DESCRIPTION: `Please enter your vehicle details so we can bring your order out to you:`,
    ENTER_YOUR_COUPON_CODE: `Enter your coupon code`,
    ENTER_YOUR_COMP_CARD_NUMBER: `Enter Your Comp Card Number`,
    ENTER_YOUR_COMP_CARD_PIN: `Enter Your Comp Card PIN`,
    ENTER_YOUR_GIFT_CARD_NUMBER: `Enter Your Gift Card Number`,
    ENTER_YOUR_GIFT_CARD_PIN: `Enter Your Gift Card PIN`,
    ENTER_YOUR_EMAIL: `Enter Your Email:`,
    ENTER_YOUR_EMAIL_VALIDATION_FAILED: `Invalid e-mail. Please try again.`,
    ENTER_YOUR_FIRST_NAME: `Enter Your First Name:`,
    ENTER_YOUR_LAST_NAME: `Enter Your Last Name Initial:`,
    ENTER_YOUR_NAME: `Enter Your Name:`,
    ENTER_YOUR_NAME_VALIDATION_FAILED: `Invalid name. Please try again.`,
    ENTER_YOUR_NAME_VALIDATION_LENGTH_FAILED: `Please enter a name.`,
    ENTER_YOUR_LAST_NAME_VALIDATION_LENGTH_FAILED: `Only one letter is allowed. Please try again.`,
    ENTER_YOUR_NAME_VALIDATION_FORMAT_FAILED: `Name must contain only letters. Please try again.`,
    ENTER_YOUR_NAME_VALIDATION_PROFANITY_FAILED: `Name contains profanity. Please try again.`,
    ERROR: `Oops!`,
    ERROR_ADDING_ITEM: `There was an error adding the item to your cart. Please try again. If the problem persists, please ask the nearest team member for assistance.`,
    ERROR_EDITING_ITEM: `There was an error editing the item in your cart. Please try again. If the problem persists, please ask the nearest team member for assistance.`,
    ERROR_AMOUNT_EXCEEDS_BALANCE: `Amount exceeds card balance.`,
    ERROR_CARD_CONFIRMATION: `Sorry! Couldn't confirm card. Please try again.`,
    ERROR_CARD_DECLINED: `Failed to charge the specified card. Please try again.`,
    ERROR_CARD_INACTIVE: `Sorry, this student card is not active.`,
    ERROR_CARD_NOT_FOUND: `Sorry! We couldn't find this card. Please try again.`,
    ERROR_CARD_NUMBER_LENGTH: `Card number is not the correct length.`,
    ERROR_CARD_RESTRICTED_TIME: `Sorry, this card cannot be used at this time.`,
    ERROR_CARD_SCHEME_NOT_ACCEPTED: `Sorry, {{$1: cardScheme}} is not accepted here. Could you please try a different card?`,
    ERROR_COUPON_CONFIRMATION: `Sorry! Couldn't use coupon code. Please try again.`,
    ERROR_INVALID_EMAIL: `Invalid email address`,
    ERROR_INVALID_LOGIN_CREDS: `Invalid username/password`,
    ERROR_INVALID_NAME: `Please enter a valid name`,
    ERROR_INVALID_PHONE_NUMBER: `Please enter a valid phone number`,
    ERROR_INVALID_ROOM_NUMBER: `Please enter a valid room number`,
    ERROR_INVALID_TABLE_NUMBER: `Please enter a valid table number`,
    ERROR_INVALID_VERIFICATION_CODE: `Invalid verification code`,
    ERROR_KIOSK_CARD_DECLINED: `Something went wrong. Your card was not charged. Please try again.`,
    ERROR_KIOSK_CARD_DECLINED_TAP: `Tap failed. Please try again.`,
    ERROR_KIOSK_PAYMENT_DEVICE_NOT_FOUND: `The kiosk could not find the payment device. Your card was not charged. Please contact a staff member.`,
    ERROR_KIOSK_PAYMENT_DEVICE_OFFLINE: `The payment device appears offline. Your card was not charged. Please contact a staff member.`,
    ERROR_ORDER_OPERATION: `Sorry, we were unable to process your order. Please try again.`,
    ERROR_PIN_LENGTH: `PIN is not the correct length.`,
    ERROR_RECEIVING_PAYMENT: `The terminal was unable to process your payment. Please try again.`,
    ERROR_RECEIVING_PAYMENT_INSTRUCTIONS: `No payment was received. This order has not been sent. Your receipt will contain a summary of your order. Please contact a staff member for further help.`,
    ERROR_SENDING_ORDER: `Hi, there seems to be an issue sending your order to the kitchen. Please contact a staff member for help.`,
    ERROR_SENDING_ORDER_INSTRUCTIONS: `Payment was successful, but there was an issue sending your order to the kitchen. Please contact a staff member for help. Your receipt will contain a summary of your order.`,
    ERROR_STARTING_ORDER: `Sorry, we were unable to start your order. Please try again. If the problem persists, please contact a staff member.`,
    ERROR_UPDATING_ORDER: `Sorry, we were unable to update your order. Please try again. If the problem persists, please contact a staff member.`,
    ERROR_VIEWING_ITEM_ALCOHOL_LIMIT: `Unable to view this item. You have reached the alcohol limit for this order. Please remove an alcoholic item to add this one.`,
    ERROR_VIEWING_ITEM_SPECIAL_OFFER_LIMIT: `Unable to view this item. You have reached the special offer limit for this order. Please remove a special offer item to add this one.`,
    EXIT: `Exit`,
    EXIT_CONFIRMATION: `Are you sure you want to exit? Exiting will clear your order.`,
    EXIT_TO_COVER: ``,
    FAVORITE: `Favorite`,
    FAVORITES: `Favorites`,
    FAVORITES_NONE_YET: `No favorites yet.`,
    FAVORITES_REMOVE_CONFIRMATION: `Are you sure you want to remove this favorite?`,
    FETCHING_RECOMMENDATIONS: `Fetching Recommendations...`,
    FIND_ACCOUNT: `Find Account`,
    FIRST_NAME: `First Name`,
    FORGOT_PASSWORD: `Forgot Password?`,
    FULFILLMENT_LABEL_ADDRESS: `Address`,
    FULFILLMENT_LABEL_GUEST_EMAIL: `Email`,
    FULFILLMENT_LABEL_GUEST_NAME: `For`,
    FULFILLMENT_LABEL_ORDER_NUMBER: `Order Number`,
    FULFILLMENT_LABEL_TABLE_NUMBER: `Table Number`,
    FULFILLMENT_LABEL_PHONE_NUMBER: `Phone Number`,
    FULFILLMENT_LABEL_TRACKER_NUMBER: `Tracker Number`,
    FULFILLMENT_LABEL_VEHICLE_DESCRIPTION: `Vehicle Description`,
    FULFILLMENT_INSTRUCTION_ADDRESS: `Your order will be delivered to the address below.`,
    FULFILLMENT_INSTRUCTION_GUEST_EMAIL: `We will send you the receipt.`,
    FULFILLMENT_INSTRUCTION_GUEST_NAME: `Your name will be called when your order is ready.`,
    FULFILLMENT_INSTRUCTION_ORDER_NUMBER: `Your order number will be called.`,
    FULFILLMENT_INSTRUCTION_PHONE_NUMBER: `You will receive a text message when your order is ready.`,
    FULFILLMENT_INSTRUCTION_TABLE_NUMBER: `Your order will be served to your table.`,
    FULFILLMENT_INSTRUCTION_TRACKER_NUMBER: `Your tracker will ring when the order is ready.`,
    FULFILLMENT_INSTRUCTION_VEHICLE_DESCRIPTION: `Your order will be brought out once you have arrived outside the restaurant.`,
    FULFILLMENT_TOGGLE: `Toggle active fulfillment method`,
    FULL_NAME: `Full Name`,
    FUTURE_ORDER_MENU_FETCH_ERROR: `Sorry! Could not find the menu for that time. Please try again.`,
    FUTURE_ORDER_NO_AVAILABLE_SLOTS: `Sorry! Could not find available future order times for you. Please try another dining option.`,
    FUTURE_ORDER_SET_FOR: `Order set for:`,
    FUTURE_ORDER_VALIDATION_WHEN_SLOT_EXPIRED_ERROR: `Sorry! Your chosen time slot is already unavailable. Please select a different time.`,
    FUTURE_ORDER_ERROR_FETCHING_SLOTS: `Something went wrong while fetching available time slots. Please try again. If the problem persists, please contact a staff member.`,
    FUTURE_ORDER_ERROR_SELECT_SLOT: `Please select a time slot.`,
    FUTURE_ORDER_TIME_SLOTS_TITLE: `Time Slots`,
    FUTURE_ORDER_TIME_SLOTS_TODAY: `Today`,
    FUTURE_ORDER_TIME_SLOTS_TOMORROW: `Tomorrow`,
    FUTURE_ORDER_PICKUP_TIME_TODAY_AT: `Pickup today at {{$1: time}}`,
    FUTURE_ORDER_PICKUP_TIME_NEXT_BUSINESS_DAY_AT: `Pickup next business day at {{$1: time}}`,
    FUTURE_ORDER_LEAD_TIME: `Estimated Order Lead Time: {{$1: time}}`,
    GENERIC_ERROR: `Oops! Something went wrong!\nPlease try again. If the problem persists, please contact a staff member.`,
    GET_REWARDS: `Get Rewards`,
    GET_RECEIPT: `Get Receipt`,
    GET_RECEIPT_DIALOG_TITLE: `Receipt Options`,
    GIFT_CARD: `Gift Card`,
    GIFT_CARD_SECONDARY: `Alternate Gift Card`,
    GIFT_CARD_TITLE: `Pay now`,
    GIFT_CARD_SUBTITLE: `Pay with the credit card device by swiping your gift card`,
    GIFT_CARD_SCANNING_ERROR_TITLE: `Gift Card Scanning Error`,
    GIFT_CARD_SCANNING_ERROR_MESSAGE: `We were unable to scan your gift card. Please try again.`,
    GIFT_CARD_APPLYING_TITLE: `Applying Gift Card`,
    GIFT_CARD_APPLYING_ERROR_TITLE: `Gift Card Applying Error`,
    GIFT_CARD_APPLYING_ERROR_MESSAGE: `We were unable to apply your gift card. Please try again.`,
    GIFT_CARD_NO_BALANCE_TITLE: `Insufficient Gift Card Balance`,
    GIFT_CARD_NO_BALANCE_MESSAGE: `Your gift card has no balance.`,
    GIFT_CARD_ADD_ANOTHER: `Add Another`,
    GIFT_CARD_ENTERING_NUMBER_ERROR_REQUIRED: `Please enter a gift card number.`,
    GIFT_CARD_ENTERING_NUMBER_ERROR_INVALID: `Please enter a valid gift card number.`,
    GIFT_CARD_ENTERING_NUMBER_ERROR_LENGTH: `Please enter a valid gift card number.`,
    GIFT_CARD_ENTERING_PIN_ERROR_REQUIRED: `Please enter a PIN.`,
    GIFT_CARD_ENTERING_PIN_ERROR_INVALID: `Please enter a valid PIN.`,
    GIFT_CARD_ENTERING_PIN_ERROR_LENGTH: `Please enter a valid PIN.`,
    GIFT_CARD_ENTERING_NUMBER_HINT: `Enter gift card number. Numbers can be {{$1: length}} digits long.`,
    GIFT_CARD_ENTERING_PIN_HINT: `Enter PIN. Numbers can be {{$1: length}} digits long.`,
    GIFT_CARD_ERROR_REMOVING: `Error removing gift card. Please try again. If the problem persists, please contact a staff member.`,
    'gift-card.select-account.title': `Select an Account`,
    'gift-card.select-account.loading': `Loading Accounts...`,
    'gift-card.select-account.error.title': `Unable to Load Accounts`,
    'gift-card.select-account.error.message': `We were unable to load your accounts. Please try again.`,
    'gift-card.select-account.column.account': `Account`,
    'gift-card.select-account.column.balance': `Current Balance`,
    'gift-card.select-account.error.toast-title': `Error`,
    'gift-card.select-account.error.toast-message': `Failed to validate. Please try again.`,
    GLOSSARY_TERM_LEARN_MORE: `Learn more`,
    GOES_WELL_WITH: `Goes well with`,
    GRAND_TOTAL: `Grand Total`,
    GROUP_ORDERING_ADD_GUEST: `Add Guest`,
    GROUP_ORDERING_GUEST_NAME_INVALID_CHARACTERS: `Guest name contains invalid characters.`,
    GROUP_ORDERING_GUEST_NAME_TAKEN: `Guest name already used. Please enter a different name.`,
    GROUP_ORDERING_GUEST_NUMBER_PREFIX: `Guest {{$1: number}}`,
    GROUP_ORDERING_ITEM_FOR: `Group Order - Who is this item for`,
    GROUP_ORDERING_LABEL: `Group Order - Item For:`,
    GROUP_ORDERING_TITLE: `Group Ordering`,
    GROUP_ORDERING_SUBTITLE: `The group ordering option will ask you which member of your party each item is for. Tap “Yes” to enable group ordering. Tap “No” if you’d like to continue.`,
    GUEST_INFO: `Guest Info`,
    GUEST_RATING_LABEL_0: `Tap to rate as 0: Very Unsatisfied`,
    GUEST_RATING_LABEL_1: `Tap to rate as 1: Somewhat Unsatisfied`,
    GUEST_RATING_LABEL_2: `Tap to rate as 2: Somewhat Satisfied`,
    GUEST_RATING_LABEL_3: `Tap to rate as 3: Very Satisfied`,
    GUEST_RATING_0: `Thank you for your feedback!`,
    GUEST_RATING_1: `Thank you for your feedback!`,
    GUEST_RATING_2: `Thank you for your feedback!`,
    GUEST_RATING_3: `Thank you for your feedback!`,
    GUEST_RATING_SUBMITTED: `Thank you for your feedback!`,
    HIDE: `Hide`,
    HOME: `Home`,
    HOW_DID_WE_DO: `How did we do? Rate us!`,
    HOW_WOULD_YOU_LIKE_TO_PAY: `How would you like to pay?`,
    HOW_WOULD_YOU_LIKE_YOUR_ORDER: `How would you like your order?`,
    KITCHEN_CLOSED: `Sorry, the kitchen is closed.`,
    ID_CHECK_LABEL: `Your order contains alcohol. You may be asked to show ID.`,
    ID_CHECK_CODE_MESSAGE: `An employee access code is required to check out with alcohol. Please ask a team member for assistance.`,
    IE_WARNING: `We noticed you are accessing our site from Internet Explorer. For the best experience please open with supported browsers like Google Chrome, Safari, Firefox, or Microsoft Edge`,
    INCLUDE_UTENSILS: `Include utensils`,
    ITEM_86D: `Hi, there seems to be an issue sending your order to the kitchen due to an item being out of stock. Please contact a staff member for help.`,
    ITEM_86D_VALIDATION: `Hi, there seems to be an issue sending your order to the kitchen due to an item being out of stock. Please contact a staff member for help.`,
    ITEM_RECIPIENT: `Made for`,
    ITEM_RECIPIENT_FOR: `Made for {{$1: recipient}}`,
    ITEM_RECIPIENT_PLACEHOLDER: ``,
    ITEM_RECOMMENDED_BADGE: `Recommended`,
    ITEM_STEP_TITLE: `Step {{$1: step}}`,
    ITEM_STEP_EMPTY: `No choices were made in this step`,
    ITEM_STEP_NO_RECOS: `No recommendations available`,
    ITEMS_NO_LONGER_IN_MENU: `Some items have been removed from your cart as they are no longer on the menu`,
    ITEM_SCAN_ERROR: `We couldn't find this item. Please add it from the menu or ask a staff member for help.`,
    ITEM_SCAN_SUCCESS: `Item added to cart!`,
    LAST_NAME: `Last Name`,
    LEAD_TIME_LONG: `Your order may need more time than your currently selected timeslot.`,
    LEAD_TIME_CART: `This order may take longer to prepare than your requested pick up time due to some selected items.`,
    LANGUAGE_SELECT_LABEL: `Tap to select language`,
    LOADING: `Loading...`,
    LOADING_MENU: `Loading the menu...`,
    LOCATION_CARD_CLOSED: `Closed`,
    LOCATION_CARD_LAST_VISITED: `Last visited`,
    LOCATION_CARD_OPEN: `Open`,
    LOCATION_CARD_SERVING_UNTIL: `until {{$1: closing time}}`,
    LOCATION_CARD_OPENS_NEXT: `opens {{$1: opening time}}`,
    LOCATION_SEARCH_BUTTON: `Search for Stores`,
    LOCATION_SEARCH_FULFILLMENT_PROMPT: `Find a store for {{$1: dine-in/take-away}}`,
    LOCATION_SEARCH_FULFILLMENT_QUESTION: `How do you want to order?`,
    LOCATION_SEARCH_HEADER: `Welcome to {{$1: org name}}`,
    LOCATION_SEARCH_INPUT_PLACEHOLDER: `Address, City and State, or Zip Code`,
    LOCATIONS_CHOOSE_A_LOCATION: `Choose a location`,
    LOCATIONS_ENTER_DIFFERENT_CODE: `Enter a different code`,
    LOCATIONS_NONE_AVAILABLE: `No locations are available yet.`,
    LOG_IN_OR_JOIN: `Login`,
    LOGIN: `Login`,
    LOGIN_GIFT_CARD: `Gift Card Login`,
    LOGIN_GIFT_CARD_SECONDARY: `Alternate Gift Card Login`,
    LOGIN_WALL_WELCOME: `Hello there!`,
    LOGIN_WALL_WELCOME_USER: `Hello, {{$1: user}}!`,
    LOGIN_WITH_CARD_NUMBER: `Log In with Card Number`,
    LOGIN_WITH_PHONE: `Log In with Phone`,
    LOOKING_UP_ACCOUNT: `Looking up account...`,
    LOOKUP_CARD: `Looking up card...`,
    LOOKUP_COUPON: `Looking up coupon...`,
    LOYALTY: `Loyalty`,
    LOYALTY_ACCOUNT_APPLY: `Apply`,
    LOYALTY_CARD_NUMBER: `Card Number`,
    LOYALTY_EXIT_TO_MENU: `Go to Menu`,
    LOYALTY_APP_AUTH_BARCODE_FALLBACK: `Scan App QR Code`,
    LOYALTY_APP_AUTH_PHONE_FALLBACK: `No app? Enter your phone number.`,
    LOYALTY_AUTH_METHOD_BARCODE: `Scan App QR Code`,
    LOYALTY_AUTH_METHOD_HID: `Tap HID Card`,
    LOYALTY_AUTH_METHOD_PHONE: `Enter your phone number.`,
    LOYALTY_AUTH_METHOD_CARD_NUMBER: `Enter your card number.`,
    LOYALTY_AUTH_METHOD_EMAIL: `Enter your email.`,
    LOYALTY_AUTH_METHOD_EMAIL_PASSWORD: `Enter your email and password.`,
    LOYALTY_AUTH_METHOD_EMAIL_PHONE: `Enter your email and phone number.`,
    LOYALTY_AUTH_METHOD_USERNAME_PASSWORD: `Enter your username and password.`,
    LOYALTY_APP_AUTH_PHONE_FALLBACK_LABEL: `Enter your account phone number:`,
    LOYALTY_APP_AUTH_SUBTITLE: `To find the barcode, open your App to the PAY screen.`,
    LOYALTY_APP_AUTH_TITLE: `Scan your App payment barcode with the scanner below.`,
    LOYALTY_APP_REDEMPTION_CODE_FALLBACK: `Manually enter 7-digit reward code`,
    LOYALTY_APP_REDEMPTION_CODE_FALLBACK_LABEL: `Enter a 7-digit reward code:`,
    LOYALTY_APPLY_REWARD_FAILED: `Failed to apply reward. Please try again.`,
    LOYALTY_APPLY_REWARD_FAILED_SPECIFIC: `Failed to apply the following rewards: {{$1: reward names}}. Please try again.`,
    LOYALTY_APPLYING_REWARD: `Applying Reward...`,
    LOYALTY_AUTH_GENERIC_ERROR: `Sorry! We couldn't find this rewards account. Please try again.`,
    LOYALTY_AUTH_INSTRUCTIONS: `Sign in to earn and spend your Rewards Program points.`,
    LOYALTY_AVAILABLE_REWARDS_LABEL: `Available Rewards`,
    LOYALTY_BALANCE_DESCRIPTION: ``,
    LOYALTY_BALANCE_LABEL: `Current Balance`,
    LOYALTY_CANNOT_MODIFY_REWARD: `Sorry - you can't modify your rewards once you've paid for the order.`,
    LOYALTY_CANNOT_REMOVE_REWARD: `Sorry, this reward cannot be removed once it's been applied to the order.`,
    LOYALTY_CARD_NUMBER_INSTRUCTIONS: `Enter your card number to earn and spend your Rewards Program points.`,
    LOYALTY_COUPON_BURNING_INCOMPATIBLE: `Sorry - you can't use your rewards because a coupon is applied.`,
    LOYALTY_GETTING_REWARDS: `Getting your rewards...`,
    LOYALTY_FETCH_REWARDS_FAILED: `Failed to get rewards. Please try again.`,
    LOYALTY_REWARD_APPLY: `Apply`,
    LOYALTY_REWARD_APPLY_FAILED: `Failed to apply rewards. Some of the rewards may not be applicable to your order.`,
    LOYALTY_REWARD_APPLIED: `Applied`,
    LOYALTY_REWARD_INVALID: `Invalid`,
    LOYALTY_REWARD_LABEL_MEAL: `Meal`,
    LOYALTY_REWARD_LABEL_MEALS: `Meals`,
    LOYALTY_REWARD_LABEL_UNSELECTED: `Redeem Now`,
    LOYALTY_REWARD_LABEL_SELECTED: `Selected`,
    LOYALTY_REWARD_LABEL_APPLIED: `Redeemed`,
    LOYALTY_REWARD_LABEL_UNAPPLIED: `Redeemed at Checkout`,
    LOYALTY_REWARD_LABEL_REMOVING: `Removing`,
    LOYALTY_REWARD_LABEL_ERROR: `Error Applying`,
    LOYALTY_REWARD_LABEL_UNAPPLICABLE: `Not Applicable`,
    LOYALTY_REWARD_GROUP_LABEL_UNKNOWN: `Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_PERCENT: `Percent Based Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_FIXED: `Fixed Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_WALLET: `Wallet Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_WALLET_AND_PAYMENT: `Wallet and Payment Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_FREE_ITEM: `Free Item Rewards`,
    LOYALTY_REWARD_GROUP_LABEL_MEAL_EQUIVALENCY: `Meal Exchange Rewards`,
    LOYALTY_REWARDS_FOR: ` for`,
    LOYALTY_NO_REWARDS_LABEL: `No Rewards`,
    LOYALTY_NO_REWARDS_NO_EARNING: `No qualifying rewards for these items.`,
    LOYALTY_NO_REWARDS_TEXT: `No qualifying rewards for these items, but you'll still earn points with this order!`,
    LOYALTY_PAYMENT: `With Loyalty`,
    LOYALTY_PAYMENT_CARD_CHARGED: `{{$1: charge_amount}} will be charged to card on file.`,
    LOYALTY_PAYMENT_FAILED: `Failed to apply payment.`,
    LOYALTY_PAYMENT_FAILED_MESSAGE: `There was a problem processing the loyalty payment. Please try again or choose another payment method.`,
    USE_LOYALTY_PAYMENT: `Use Loyalty`,
    LOYALTY_PAYMENT_SUBMIT: `Make Payment`,
    LOYALTY_PAYMENT_REWARD: `Charged to Loyalty`,
    LOYALTY_PAYMENT_REWARD_DESCRIPTION: `Your card on file with the provider will be charged for this transaction.`,
    LOYALTY_PAY_WITH_APP: `Pay with App`,
    LOYALTY_EARNED_POINT_ESTIMATE: `Estimated Reward Points for This Order:`,
    LOYALTY_EARNED_POINT_ESTIMATE_METRIC: `{{$1: number }} points`,
    LOYALTY_INTRO_ACCOUNT_STATUS: `Account Status`,
    LOYALTY_INTRO_COULD_NOT_FIND_ACCOUNT: `We couldn't find your account.`,
    LOYALTY_INTRO_LOGIN_AND_EARN: `Please log in to your account below to earn points and redeem rewards.`,
    LOYALTY_INTRO_PHONE_SIGN_UP: `Sign Up with Phone Number`,
    LOYALTY_INTRO_PHONE_SIGN_UP_DESCRIPTION: `You can still earn points on your order today by scanning the order receipt after you sign up!`,
    LOYALTY_INTRO_QR_SIGN_IN: `Please scan your QR code below to sign in.`,
    LOYALTY_INTRO_QR_SIGN_UP: `Please scan the QR code below to sign up.`,
    LOYALTY_INTRO_WELCOME_BACK: `Welcome, `,
    LOYALTY_FETCH_REWARDS_FAILED_NON_UNIQUE: `The phone number you provided is associated with more than one loyalty account or does not exist.`,
    LOYALTY_REDEMPTION_CODE: `Apply Redemption Code`,
    LOYALTY_REMOVING_REWARD: `Removing Reward...`,
    LOYALTY_SCAN_FAILED_TITLE: `Scan Failed`,
    LOYALTY_SCAN_FAILED_MESSAGE: `We couldn't scan your QR code. Please try again.`,
    LOYALTY_SIGN_OUT_NOTICE: `You will be automatically signed out when your order is placed or you exit the menu.`,
    LOYALTY_SIGN_OUT_NOTICE_PASSWORD: `You will be automatically signed out when your order is placed or you exit the menu. Passwords are never saved.`,
    LOYALTY_SIGN_UP_SMS_SENT: `Sign up message was sent!`,
    LOYALTY_SIGN_UP_BACK_TO_LOGIN: `Back to Login`,
    LOYALTY_SIGN_UP_SUCCESS_TITLE: `Sign Up Successful!`,
    LOYALTY_SIGN_UP_WITH_QR: `Sign Up with QR Code`,
    LOYALTY_SIGN_UP_WITH_PHONE_NUMBER: `Sign Up with Phone Number`,
    LOYALTY_WELCOME_TITLE: `Earn and spend rewards`,
    LOYALTY_SIGNUP_TITLE: `Get a sign-up link sent to your phone`,
    LOYALTY_YOU_CURRENTLY_HAVE: `You currently have`,
    LOYALTY_NAV_REWARDS: `My Rewards`,
    LOYALTY_NAV_ORDER_HISTORY: `My Orders`,
    LOYALTY_SIGN_IN_ATTEMPTS_EXCEEDED: `You have exceeded the maximum number of sign-in attempts.`,
    GIFT_CARD_ACCESS_ATTEMPTS_EXCEEDED: `You have exceeded the maximum number of gift card access attempts.`,
    MAKE_IT_A_COMBO: `Make It A Combo?`,
    MAKE_IT_A_COMBO_MOD: `Make It A Combo?`,
    MAKE_IT_A_COMBO_ACCEPT: `Yes, please!`,
    MAKE_IT_A_COMBO_REJECT: `No, thanks!`,
    MAKE_IT_A_COMBO_NO: `No thanks, no combo for me`,
    MAKE_IT_A_COMBO_YES: `Make It A Combo?`,
    MAKE_IT_A_COMBO_ACCEPTED_TITLE: `Great Choice!`,
    MAKE_IT_A_COMBO_ACCEPTED_MESSAGE: `Choose your side and drink down below!`,
    MAKE_IT_A_COMBO_CHECKOUT_UPSELL_SUBTITLE: `Add a side and drink to make it a combo!`,
    MENU_COVER_ALT_TEXT: `Tap here to start ordering`,
    MENU_COVER_LOYALTY_TEXT: `Log In to loyalty to earn points and redeem rewards`,
    MENU_COVER_QUIZ_TEXT: `Take the quiz to find out which item is right for you!`,
    MENU_COVER_WAYFINDER_TEXT: `Go to our way-finder to find your way around`,
    MAINTENANCE_COVER_TITLE: `This kiosk is under maintenance`,
    MAINTENANCE_HID_DISCONNECTED_MESSAGE: `Please ensure the HID is connected to the kiosk.`,
    MAINTENANCE_HID_DISCONNECTED_TITLE: `HID Disrupted`,
    MAINTENANCE_KDS_DISCONNECTED_MESSAGE: `Please ensure the KDS is connected to the kiosk.`,
    MAINTENANCE_KDS_DISCONNECTED_TITLE: `KDS Disrupted`,
    MAINTENANCE_KIOSK_UNAVAILABLE_MESSAGE: `Please try another kiosk or ask a staff member for help.`,
    MAINTENANCE_KIOSK_UNAVAILABLE_TITLE: `This kiosk is currently unavailable`,
    MAINTENANCE_MSR_DISCONNECTED_MESSAGE: `Please ensure the Magnetic Stripe Reader is connected to the kiosk.`,
    MAINTENANCE_MSR_DISCONNECTED_TITLE: `Magnetic Stripe Reader Disrupted`,
    MAINTENANCE_NETWORK_DISCONNECTED_MESSAGE: `Please ensure the kiosk is connected to the network.`,
    MAINTENANCE_NETWORK_DISCONNECTED_TITLE: `No Network Connection`,
    MAINTENANCE_NETWORK_UNSTABLE_MESSAGE: `This kiosk is having trouble connecting due to low internet speed.\n\nPlease check that your connection is stable.\n\nRecommended Speed: Minimum 10 Mbps upload & download.`,
    MAINTENANCE_NETWORK_UNSTABLE_TITLE: `Unstable Network Detected`,
    MAINTENANCE_PAYMENT_CLIENT_DISCONNECTED_MESSAGE: `Please ensure the payment device is on and connected to the kiosk.`,
    MAINTENANCE_PAYMENT_CLIENT_DISCONNECTED_TITLE: `Payment Device Disrupted`,
    MAINTENANCE_PRINTER_COVER_OPEN_MESSAGE: `Please ensure the printer cover is closed.`,
    MAINTENANCE_PRINTER_COVER_OPEN_TITLE: `Printer Cover Open`,
    MAINTENANCE_PRINTER_DISCONNECTED_MESSAGE: `Please ensure the printer is connected to the kiosk.`,
    MAINTENANCE_PRINTER_DISCONNECTED_TITLE: `Printer Disrupted`,
    MAINTENANCE_PRINTER_OUT_OF_PAPER_MESSAGE: `Please ensure the printer has paper.`,
    MAINTENANCE_PRINTER_OUT_OF_PAPER_TITLE: `Printer Out of Paper`,
    MAINTENANCE_SCALE_DISCONNECTED_MESSAGE: `Please ensure the scale is connected to the kiosk.`,
    MAINTENANCE_SCALE_DISCONNECTED_TITLE: `Scale Disrupted`,
    MAINTENANCE_SCANNER_DISCONNECTED_MESSAGE: `Please ensure the scanner is connected to the kiosk.`,
    MAINTENANCE_SCANNER_DISCONNECTED_TITLE: `Scanner Disrupted`,
    MAX_ALCOHOLIC_ITEM_LIMIT_REACHED: `Max allowed alcoholic beverage limit per order is {{$1: number}}`,
    MAX_SPECIAL_ITEM_LIMIT_REACHED: `Max allowed special item limit per order is {{$1: number}}`,
    MENU_COVER_DINING_OPTIONS: `How would you like to order?`,
    MENU_COVER_LANGUAGE_OPTIONS: `Select a language to begin`,
    MENU_COVER_PROMO_ALT_TEXT_ITEM: `Tap here to start the order and view this promoted item: {{$1: item name}}!`,
    MENU_COVER_PROMO_ALT_TEXT_SECTION: `Tap here to start the order and go directly to this promoted section: {{$1: section name}}!`,
    MENU_COVER_PROMO_ALT_TEXT_PAGE: `Tap here to start the order and go directly to this promoted page: {{$1: page name}}!`,
    MENU_COVER_DINING_OPTION_ALT_TEXT: `Tap here to start the order for "{{$1: fulfillment method}}"!`,
    MENU_COVER_BACK_TO_DINING_OPTIONS: `Back to Dining Options`,
    MENU_COVER_BACK_TO_PHONE_NUMBER: `Back to Phone Number`,
    MARKETING_CONSENT: `I want to receive news, updates, discounts, and offers`,
    MENU_FILTERS_DIETARY_FILTERS: `Dietary`,
    MENU_FILTERS_DIETARY_FILTERS_DESCRIPTION: `Select any dietary labels that apply. They'll appear on the menu.`,
    MENU_FILTERS_NUTRITION_PREFERENCE: `Nutrition Preferences`,
    MENU_FILTERS_OTHER_FILTERS: `Other`,
    MENU_FILTERS_OTHER_FILTERS_DESCRIPTION: ``,
    MENU_FILTERS_RESTRICTIVE_FILTERS: `Restrictive`,
    MENU_FILTERS_RESTRICTIVE_FILTERS_DESCRIPTION: `If you have certain intolerances, select one of these to hide them from the menu`,
    MENU_FILTERS_COUNT: `{{$1: number}} Nutrition Preferences active. Tap to edit.`,
    MOD_86D: `Hi, there seems to be an issue sending your order to the kitchen due to an item being out of stock. Please contact a staff member for help. Note that your payment WAS accepted by restaurant.`,
    MOD_GROUP_AGGREGATE_QUANTITY: `{{$1: number range, e.g. 2-3}} items in total`,
    MOD_GROUP_CHOICES_INCREMENT: `multiples of {{$1: number, e.g. you have to get 2/4/6 sauces}}`,
    MOD_GROUP_CHOICES_MAKE_ONE: `make one unique choice`,
    MOD_GROUP_CHOICES_MAKE_X: `make {{$1: number range, e.g. 2-3}} different choices`,
    MOD_GROUP_CHOICES_QUANTITY_PER: `{{$1: number range, e.g. 2-3}} per choice`,
    MOD_GROUP_CHOOSE_AT_LEAST_X: `Choose at least {{$1: number}}`,
    MOD_GROUP_CHOOSE_BETWEEN_X_AND_Y: `Choose between {{$1: number}} and {{$2: number}}`,
    MOD_GROUP_CHOOSE_ONE: `Choose one`,
    MOD_GROUP_CHOOSE_UP_TO_ONE: `Choose up to one`,
    MOD_GROUP_CHOOSE_UP_TO_X: `Choose up to {{$1: number}}`,
    MOD_GROUP_CHOOSE_X: `Choose {{$1: number}}`,
    MOD_GROUP_DEEMPHASIZE_SHOW: `Show - {{$1: mod_group}}`,
    MOD_GROUP_DEEMPHASIZE_HIDE: `Hide - {{$1: mod_group}}`,
    MOD_GROUP_WALLET_$_TO_SPEND: `{{$1: dollar amount}} to spend before being charged.`,
    MOD_GROUP_WALLET_ADDITIONAL_CHOICES_AT_$_PER: `Additional choices at {{$1: dollar amount}} per choice.`,
    MOD_GROUP_WALLET_FIRST_X_FREE: `First {{$1: number}} are free.`,
    MOD_GROUP_WALLET_FIRST_SINGLE_FREE: `First one is free.`,
    MOD_GROUP_WALLET_UP_TO_X: `Up to {{$1: number}} at {{$2: dollar amount}} per choice`,
    MOD_GROUP_SUBSTITUTION_MOD_CANCEL: `Close Substitution`,
    MOD_GROUP_SUBSTITUTION_MOD_CHANGE: `Change Substitution`,
    MOD_GROUP_SUBSTITUTION_MOD_SELECT: `Substitute`,
    MOD_GROUP_SUBSTITUTION_UNSELECTED: `Add to Item`,
    MOD_RECOMMENDED_BADGE: `Recommended`,
    MOD_UPSELL_PROMPT_TITLE: `Add Option?`,
    MOD_UPSELL_PROMPT_ACCEPT: `Add`,
    MOD_UPSELL_PROMPT_DECLINE: `No Thanks`,
    MODIFY: `Modify`,
    MODIFY_INGREDIENTS: `Modify Ingredients`,
    MORE: `more`,
    NAME_CALLED_ORDER_READY: `Your name will be called when your order is ready.`,
    NAME_ON_CARD: `Name on Card`,
    NEED_MORE_TIME: `Need more time?`,
    NEW_PASSWORD: `New Password`,
    NEXT: `Next`,
    NO: `No`,
    NO_ITEMS_MATCH_FILTERS: `No items match your filters.`,
    NO_ORDERS_MATCH: `No orders match your search text.`,
    NO_THANKS: `No, Thanks`,
    NO_TIP: `No Tip`,
    NOT_A_MEMBER: `Not a member?`,
    NUTRITION_INFO_CALORIES: `Cals`,
    NUTRITION_INFO_PROTEIN: `Protein`,
    NUTRITION_INFO_FAT: `Fat`,
    NUTRITION_INFO_CARBS: `Carbs`,
    NUTRITION_INFO_SUGAR: `Sugar`,
    OK: `OK`,
    OR: `or`,
    OPT_OUT_OF_RECOMMENDATIONS: `Opt-out of Recommendations`,
    ORDER_GET_ERROR: `Sorry, we had trouble creating your order. Please try again.`,
    ORDER_HISTORY: `Order History`,
    ORDER_HISTORY_EMPTY: `There are no orders in your history. Once you place an order, it will appear here.`,
    ORDER_HISTORY_ERROR: `Sorry, we had trouble loading your order history. Please try again.`,
    ORDER_SENT: `Order Sent`,
    ORDER_SUMMARY: `Order Summary`,
    ORDER_SUMMARY_WITH_NAME: `Order Summary for {{$1 guest name}}`,
    ORDER_SUMMARY_LABEL: `Order Summary. {{$1 fulfillment}} Order`,
    ORDER_SUMMARY_LABEL_WITH_NAME: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}}`,
    ORDER_SUMMARY_LABEL_WITH_TABLE: `Order Summary. {{$1 fulfillment}} Order for Table {{$2: table number}}`,
    ORDER_SUMMARY_LABEL_WITH_TRACKER: `Order Summary. {{$1 fulfillment}} Order for tracker #{{$2: tracker number}}`,
    ORDER_SUMMARY_LABEL_WITH_NAME_AND_TABLE: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}} at Table {{$3: table number}}`,
    ORDER_SUMMARY_LABEL_WITH_NAME_AND_TRACKER: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}} for Tracker #{{$3: tracker number}}`,
    ORDER_SUMMARY_LABEL_WITH_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for Order #{{$2: order number}}`,
    ORDER_SUMMARY_LABEL_WITH_TRACKER_AND_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for Tracker #{{$2: tracker number}} for Order #{{$3: order number}}`,
    ORDER_SUMMARY_LABEL_WITH_TABLE_AND_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for Table {{$2: table number}} for Order #{{$3: order number}}`,
    ORDER_SUMMARY_LABEL_WITH_NAME_AND_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}} for Order #{{$3: order number}}`,
    ORDER_SUMMARY_LABEL_WITH_NAME_TABLE_AND_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}} at Table {{$3: table number}} for Order #{{$4: order number}}`,
    ORDER_SUMMARY_LABEL_WITH_NAME_TRACKER_AND_ORDER_NUMBER: `Order Summary. {{$1 fulfillment}} Order for {{$2 guest name}} at TRACKER {{$3: tracker number}} for Order #{{$4: order number}}`,
    ORDER_THROTTLE_MAX_EXCEEDED: `Sorry! The restaurant is experiencing more demand than it can handle. Please try with a different order time.`,
    ORDER_TIME_ASAP: `Place an order ASAP`,
    ORDER_TIME_LATER: `Later today`,
    ORDER_TIME_NEXT_BUSINESS_DAY: `Next Business Day`,
    ORDER_TIME_FUTURE: `Order for Later`,
    ORDER_TOTAL_MAX_EXCEEDED: `For large orders, please see a staff member.`,
    ORDER_UPDATE_FAILED: `Failed to update order.`,
    ORDER_VALIDATION_IN_PROGRESS: `Validating your order`,
    ORDER_VALIDATION_FAILED: `Oops! Something went wrong!`,
    ORDER_WAS_PLACED: `Your order was placed.`,
    ORDERING_MODE_CHANGED: `Ordering mode changed. Your session has been reset.`,
    ORDERED_ITEM_MAKE_IT_A_COMBO_LABEL: `Tap here to make the {{$1: ordered item name}} a combo.`,
    ORDERED_ITEM_COMBO_LABEL: `Combo`,
    ORDERING_ITEM_ABSENCE_RESET: `This item was out of date. The item has been reset to it's initial state. Please try again.`,
    OUR_MENU: `Our Menu`,
    OUTPOST_PICKUP: `Outpost Pickup`,
    OUTPOST_ROOM_NUMBER_INSTRUCTION: `Enter your outpost room number`,
    OUTPOST_ROOM_NUMBER_TITLE: `Room Number`,
    OUT_OF_STOCK: `Out of Stock`,
    PAGE_NAVIGATION_TITLE: `Tap one of the options below to continue to a specific page in the menu. {{$1: total}} pages available.`,
    PAGE_NOT_FOUND: `Page not found`,
    PAGE_NOT_FOUND_MESSAGE: `This page doesn’t exist or may have moved.`,
    PAGE_ERROR: `We couldn’t load this page`,
    PAGE_ERROR_MESSAGE: `Try again, or return to the home page.`,
    GO_TO_HOME_PAGE: `Go Home`,
    PARTIAL_PAYMENT_LINE: `Partial payment`,
    PARTIAL_PAYMENT_WARNING_TITLE: `Partial Payment`,
    PARTIAL_PAYMENT_WARNING_MESSAGE: `There is a partial payment on this order. If want to go back, your payment will first be refunded.`,
    PASSWORD: `Password`,
    PASSWORD_REQUIREMENTS: `Use 14 to 32 characters, including an uppercase letter, a lowercase letter, and a number.`,
    PASSWORD_UPDATED: `Password updated`,
    PASSWORD_UPDATED_LOG_IN_AGAIN: `You can now log in with your new password.`,
    PARTIAL_TENDER_TIMEOUT: `Hi, if you do not complete the payment within the next 5 minutes, we will cancel the order and refund your payment.`,
    PAY_AT_CASHIER: `Please proceed to the cashier to pay.`,
    PAY_NOW: `Pay Now`,
    PAY_WITH: `Pay With`,
    PAY_WITH_CREDIT: `Pay with Credit Card`,
    PAY_WITH_DEBIT_CREDIT: `Pay with Debit/Credit`,
    PAYMENT: `Payment`,
    PAYMENT_DETAILS: `Payment Details`,
    PAYMENT_LINE_LABEL_DONATION: `Donation for {{$1: amount}}`,
    PAYMENT_LINE_LABEL_SUBTOTAL: `Subtotal is {{$1: amount}}`,
    PAYMENT_LINE_LABEL_PARTIAL: `Partial payment for {{$1: amount}}`,
    PAYMENT_LINE_LABEL_GIFT_CARD: `Gift card redemption from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_COUPON: `Coupon discount from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_COMP_CARD: `Coupon discount from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_EBT: `EBT payment for {{$1: amount}}`,
    PAYMENT_LINE_LABEL_LOYALTY: `Loyalty redemption from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_REWARD: `Reward redemption from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_DISCOUNT: `Discount from {{$1: name}} for {{$2: amount}}`,
    PAYMENT_LINE_LABEL_TAX: `Tax is {{$1: amount}}`,
    PAYMENT_LINE_LABEL_TIP: `Tip is {{$1: amount}}`,
    PAYMENT_LINE_LABEL_SERVICE_CHARGE: `Service charge of {{$1: amount}}`,
    PAYMENT_LINE_LABEL_NAMED_SERVICE_CHARGE: `Service charge of {{$1: amount}} for {{$2: name}}`,
    PAYMENT_LINE_LABEL_TOTAL: `Grand total is {{$1: amount}}`,
    PAYMENT_LINE_LABEL_REMAINING: `Remaining balance is {{$1: amount}}`,
    PAYMENT_LINE_LABEL_PAID: `Paid amount is {{$1: amount}}`,
    PAYMENT_OPTION_LABEL_CASH: `Tap to pay with Cash`,
    PAYMENT_OPTION_LABEL_CARD: `Tap to pay with a Credit Card`,
    PAYMENT_OPTION_LABEL_COUPON: `Tap to redeem a Coupon`,
    PAYMENT_OPTION_LABEL_COMP_CARD: `Tap to redeem a Comp card`,
    PAYMENT_OPTION_LABEL_DONATION: `Tap to make or edit Donations`,
    PAYMENT_OPTION_LABEL_EBT: `Tap to pay with EBT`,
    PAYMENT_OPTION_LABEL_EBT_REFUND: `Tap to refund EBT`,
    PAYMENT_OPTION_LABEL_EBT_CASH_BENEFIT: `Tap to pay with EBT Cash Benefit`,
    PAYMENT_OPTION_LABEL_EBT_FOOD_STAMPS: `Tap to pay with EBT Food Stamps`,
    PAYMENT_OPTION_LABEL_GIFT_CARD: `Tap to pay with a Gift Card`,
    PAYMENT_OPTION_LABEL_LOYALTY: `Tap to redeem rewards`,
    PAYMENT_OPTION_LABEL_LOYALTY_PAYMENT: `Tap to redeem rewards`,
    PAYMENT_OPTION_LABEL_FULLY_PAID: `Tap to complete payment`,
    PAYMENT_PROMPT_NFC: `Pay with the credit card device by inserting your card or tapping your phone.`,
    PAYMENT_PROMPT_DIP: `Pay with the credit card device by inserting your card.`,
    PAYMENT_SUCCESSFUL: `Payment Successful`,
    PAYMENT_SUCCESSFUL_SUBTITLE: ``,
    PAYMENT_KIOSK_ERROR: `Sorry, your payment failed. Please try again.`,
    PAYMENT_KIOSK_TIMEOUT: `Sorry, the payment session timed out. Please try again.`,
    PAYMENT_TOTAL: `Payment total`,
    PAYMENT_WAS_TAKEN: `Please note your payment was taken.`,
    PAYMENT_WAS_NOT_TAKEN: `Please note your payment was not taken. This is not a confirmation of payment.`,
    PHONE_NUMBER: `Phone Number`,
    PICK_UP: `Pick Up`,
    PICK_UP_AT: `Pick up at`,
    PICK_UP_ENTER_NUMBER: `Please pick up a number and enter it below:`,
    PICK_UP_ENTER_NUMBER_WITH_NAME: `Hey {{$1: guest name}}, please pick up a number and enter it below:`,
    PICK_UP_ENTER_NUMBER_TRACKER: `Please pick up a tracker and enter its number:`,
    PICK_UP_ENTER_NUMBER_TRACKER_WITH_NAME: `Hey {{$1: guest name}}, please pick up a tracker and enter its number:`,
    PLACE_ORDER: `Place Order`,
    PLEASE_ENTER_A_VALID_AMOUNT: `Please enter a valid amount to refund.`,
    PLEASE_USE_TEST_CARD: `This location is still in test mode. Please use a test card to pay.`,
    PLEASE_WAIT: `Please wait...`,
    PRICE: `Price is {{$1:price}}`,
    PRINTING_RECEIPT: `Printing your receipt...`,
    PRINT_RECEIPT: `Print Receipt`,
    PRINT_RECEIPT_ERROR: `Sorry, there was an error printing your receipt. Please try again later.`,
    PRINTED_RECEIPT_COMMAND: `Please remember to take your receipt!`,
    PROCESSING_PAYMENT: `Processing payment...`,
    PROMOTED_MODIFIER: `Promoted!`,
    QUANTITY_SELECT: `{{$1:quantity}} Selected`,
    QUANTITY_SELECT_INCREASE: `Increase Quantity`,
    QUANTITY_SELECT_DECREASE: `Decrease Quantity`,
    QUANTITY_SELECT_LABEL: ``,
    QUIT: `Quit`,
    QUICK_CHECKOUT: `Quick Checkout`,
    QUICK_NAV_TAP_TO_VIEW: `Tap to View:`,
    QUIZ_RESULTS_TITLE: `Results`,
    QUIZ_RESULTS_EMPTY: `No results found. Try again with a different set of answers.`,
    QUIZ_RESULTS_CAROUSEL_NEXT_LABEL: `Tap to view next result. {{$1:index}} of {{$2:total}}`,
    QUIZ_RESULTS_CAROUSEL_PREV_LABEL: `Tap to view previous result. {{$1:index}} of {{$2:total}}`,
    QUIZ_STEP_ERROR: `Please answer all questions before continuing.`,
    QUIZ_STEP_ANSWER_LABEL: `Tap to Answer with {{$1:badge name}}:`,
    QR_CODE_RECEIPT_INSTRUCTIONS: `Scan the QR code to get a copy of your receipt on your phone.`,
    RECEIPT_AUTHORIZATION: `Authorization`,
    RECEIPT_CARD_NUMBER: `Card#`,
    RECEIPT_CHECK_ID: `CHECK ID`,
    RECEIPT_DISCOUNTS: `Discounts`,
    RECEIPT_DONATION: `Donation`,
    RECEIPT_FAILED: `Hi, there seems to be an issue sending your order to the kitchen.\n\nPlease contact a staff member for help.`,
    RECEIPT_FAILED_ITEM_86D: `Hi, there seems to be an issue sending your order to the kitchen due to an item being out of stock. Please contact a staff member for help.`,
    RECEIPT_LOYALTY_POINTS_EARNED_TEXT: ``,
    RECEIPT_MENU_ITEMS_WITHOUT_BARCODE: `The following items were not included in the QR Code because they don't have a PLU associated with them:`,
    RECEIPT_NOT_RECEIPT: `This is not a receipt or proof of purchase.`,
    RECEIPT_ORDER: `ORDER`,
    RECEIPT_ORDER_TOTAL: `Order Total`,
    RECEIPT_PAY_AT_CASHIER: `PLEASE PAY AT THE CASHIER`,
    RECEIPT_PICK_UP_AT_LABEL: `Pick Up At`,
    RECEIPT_PICKUP_TIME_LABEL: `Pickup Time`,
    RECEIPT_DELIVERY_TIME_LABEL: `Delivery Time`,
    RECEIPT_PRINTED: `Receipt Printed`,
    RECEIPT_REFUNDED: `Refunded`,
    RECEIPT_REMAINING_BALANCE: `Remaining Balance`,
    RECEIPT_SELF_SERVE: `Self-serve:`,
    RECEIPT_SPECIAL_REQUEST: `Special Request`,
    RECEIPT_SUB_TOTAL: `Subtotal`,
    RECEIPT_TAX_TOTAL: `Tax`,
    RECEIPT_TIP_THANKS: `Tip (Thanks!)`,
    RECEIPT_SERVICE_CHARGE: `Service Charge`,
    RECEIPT_TRANS_NO: `KIOSK TRANS NO`,
    RECEIPT_YOUR_CHECK_NUMBER: `Your check number is`,
    RECOMMENDATIONS: `Recommendations`,
    RECOMMENDED_FOR_YOU: `Recommended for you`,
    REFUND: `Refund`,
    REFUNDER_ALREADY_REFUNDED: `Refunded`,
    REFUNDER_DELAY_MESSAGE: `Refunds may take a few days to appear on your account statement.`,
    REFUNDER_INSTRUCTIONS: `Search for orders using the Authorization number on the receipt, order number, email address, phone number, last four digits of the card, transaction reference number, or orderId.`,
    REFUNDER_SEARCH_PLACEHOLDER: `Receipt Authorization Number, Order Number, or Card Last Four Digits`,
    REFUNDER_TITLE: `Refund an Order`,
    REFUNDER_SEARCHING_TITLE: `Searching`,
    REFUNDER_RESULTS_TITLE: `Orders`,
    REFUNDER_RESULTS_EMPTY: `No orders found. Please double check your search criteria and try again.`,
    REFUNDER_REFUNDING_TITLE: `Refunding`,
    REFUNDER_REFUND_ERROR: `There was an error processing your refund. Please try again. If the issue persists, please contact support.`,
    REFUNDER_REFUND_SUCCESS: `Refund successful! The transaction will be processed shortly.`,
    REFUNDER_REFUND_SUCCESS_BACK: `Search again`,
    REFUNDER_SEARCH_INPUT_ERROR: `Please enter a valid Authorization Number, Order Number, or Card Last Four Digits.`,
    REFUNDER_SEARCH_ERROR: `There was an error searching for your order. Please try again. If the issue persists, please contact support.`,
    REFUNDING: `Processing Refund...`,
    REFUND_FAILED: `Refund failed. Please try again later.`,
    REMOVE: `Remove`,
    REMOVE_CARD: `Don't forget to remove your card.`,
    REMOVE_COMP_CARD: `Remove Comp Card`,
    REMOVE_COUPON: `Remove Coupon`,
    REMOVE_GIFT_CARD: `Remove`,
    REMOVE_TIP: `Remove Tip`,
    REMOVING_COUPON: `Removing coupon...`,
    REORDER: `Order again`,
    REPEAT_PASSWORD: `Repeat Password`,
    RESET: `Reset`,
    REWARDS: `Rewards`,
    REWARDS_LOGGED_IN: `Rewards`,
    REWARDS_APPLICABLE_TO_ORDER: `Applicable now to your order`,
    REWARDS_NOT_APPLICABLE_TO_ORDER: `Not yet applicable to your order`,
    REWARDS_EDIT: `Edit Rewards`,
    REWARDS_BALANCE_POINT_LABEL: `{{$1: number}} points`,
    REWARDS_BALANCE_MEAL_EQUIVALENCY_LABEL: `{{$1: number}} meals`,
    REWARDS_COST_POINT_LABEL: `{{$1: number}} points`,
    SAVE: `Update`,
    SAVE_CARD: `Save card on account for future purchases.`,
    SCAN_COUPON_BELOW: `You can scan coupons with the scanner below.`,
    SCAN_YOUR_GIFT_CARD: `Scan Your Gift Card`,
    SEARCH: `Search`,
    SEARCH_BEGIN: `Enter your search term below to begin searching.`,
    SEARCH_EMPTY: `No results found. Try a different search term.`,
    SEARCH_HINT: `Search for items using any combination of name or description.`,
    SECRET_MENU_IMAGE_ALT: `Tap to view the secret menu`,
    SELECT_DATE: `Select Date`,
    SELF_SERVE: `Self-serve`,
    SEND: `Send`,
    SEND_LOYALTY_SMS: `Send SMS`,
    SEND_ORDER_UI_TIMEOUT: `Hi, we're still attempting to send your order to the kitchen. Please contact a staff member for help.`,
    SENT_LOYALTY_SIGNUP_SMS: `SMS was sent! Check your phone and sign up at your convenience.`,
    SENDING_ORDER_KITCHEN: `Sending order to kitchen...`,
    SENDING_ORDER_VALIDATION: `Confirming order...`,
    SENDING_RECEIPT: `Sending Receipt...`,
    SENDING_SMS: `Sending SMS...`,
    SERVICE_CHARGE: `Service Charge`,
    SHOW: `Show`,
    SHOW_NUTRITION: `Show Nutrition Facts`,
    SHOW_STAFF_FIRST: `Please show a staff member this message before exiting.\nReally exit?`,
    SIGNUP_CTA: `Sign up for an account!`,
    SIGN_OUT: `Sign Out`,
    SIGN_UP: `Sign Up`,
    SIGNUP_BENEFITS: `Create an account to save your credit card and favorite items for easy checkout.`,
    SIGNUP_LOYALTY: `Sign Up For Loyalty`,
    SKIP: `Skip`,
    SPECIAL_REQUESTS: `Special Requests`,
    SPECIAL_REQUESTS_EXAMPLE: `ex: sauce on side, no anchovies, etc.`,
    SPECIAL_REQUESTS_FINEPRINT: `Substitutions and requests may incur additional charge.`,
    SPECIAL_REQUESTS_ADD: `Add Special Request`,
    SPECIAL_REQUESTS_ERROR_TOO_LONG: `Special request is too long.`,
    SPECIAL_REQUESTS_ERROR_NO_INVALID_CHARACTERS: `Please only use letters, numbers, and punctuation.`,
    SPECIAL_REQUESTS_ERROR_NO_VALUE: `Please enter a special request.`,
    SR_REMOVE_ITEM_FROM_CART: `Remove item from cart.`,
    STARTING_ORDER: `Starting Order...`,
    UPDATING_ORDER: `Updating Order...`,
    START_ORDER_BY_SCANNING: `Start your order by scanning an item at the bottom of the kiosk.`,
    START_NEW_ORDER: `Start another order`,
    STORED_VALUE_REMAINING_BALANCE: `remaining`,
    STORE_INFO: `Store Info`,
    STORE_INFORMATION: `Store Information`,
    STUDENT_CARD: `Student Card`,
    STUDENT_CARD_INSUFFICIENT_FUNDS: `Sorry, this student card is out of funds.`,
    STUDENT_CARD_SUBTITLE_HID: `Tap your student card to pay`,
    STUDENT_CARD_SUBTITLE_HID_MSR: `Tap or swipe your student card to pay`,
    STUDENT_CARD_SUBTITLE_MSR: `Swipe your student card to pay`,
    SUBMIT: `Submit`,
    SUBTOTAL: `Subtotal`,
    SUBTOTAL_REDUCED: `Reduced Subtotal`,
    SUBTOTAL_INCREASED: `New Subtotal`,
    SUBTOTAL_WARNING_COMBO_SAVE_TITLE: `You're saving with a combo!`,
    SUBTOTAL_WARNING_COMBO_SAVE_LINE: `You Save`,
    SUCCESS: `Success`,
    SUMMARY_STEP_FOOTNOTE: ``,
    SUMMARY_STEP_NOTICE: ``,
    SWITCH_LOCATION: `Switch Location`,
    SWITCH_TO_CATERING: `Switch to Catering`,
    SWITCH_TO_WEB: `Switch to Online Ordering`,
    SWIPE_YOUR_GIFT_CARD: `Swipe your gift card to pay`,
    SWIPE_YOUR_COMP_CARD: `Swipe your comp to pay`,
    SWIPE_OR_SCAN_YOUR_GIFT_CARD: `Swipe or scan your gift card to pay`,
    TAKING_LONGER_THAN_USUAL: `Taking longer than usual...`,
    TAP_HERE_FOR_LANGUAGE: `Tap Here for {{$1: language, e.g. 'English'}}`,
    TAP_OUTSIDE_CLOSE: `Tap outside to close`,
    TAP_TO_REDEEM: `Tap to redeem`,
    TAXES: `Tax`,
    TERMS_OF_SERVICE: `Terms of Service`,
    TERMS_OF_SERVICE_LOAD_FAILED: `Error while loading the Terms of Service`,
    TEXT_RECEIPT: `Text Receipt`,
    TEXT_RECEIPT_SEND_FAILED: `Could not send text receipt.`,
    TEXT_RECEIPT_SEND_SUCCESS: `Receipt sent!`,
    TEXT_YOU_WHEN_READY: `We'll text you when your order is ready.`,
    THANK_YOU: `Thank you!`,
    THEN_PRESS_BUTTON_1: `Then press the`,
    THEN_PRESS_BUTTON_2: `button.`,
    TIP: `Tip`,
    TIPPING: `Tipping...`,
    TIP_AMOUNT_TOO_HIGH: `The tip amount is too high.`,
    TIP_AMOUNT_TOO_LOW: `The tip amount is too low.`,
    TIP_FAILED: `Sorry! Could not add a tip. Please try again.`,
    TIP_CANNOT_BE_MODIFIED: `Sorry! You can't modify your tip once you've paid for the order.`,
    TIP_LABEL_PERCENT: `Tap to add a {{$1: percent}}% ({{$2: amount}}) tip`,
    TIP_LABEL_FIXED: `Tap to add a {{$1: amount}} tip`,
    TIP_LABEL_CUSTOM: `Tap to add a custom tip`,
    TIP_LABEL_NONE: `Tap to remove tip`,
    TO_GO: `To Go`,
    TOTAL: `Total`,
    TOTAL_CHANGED_SUBTOTAL_TEXT: `The prices of some items in your order have changed.`,
    TOTAL_CHANGED_SUBTOTAL_LEADING_TEXT: `The subtotal changed and is now`,
    TOTAL_CHANGED_LEADING_TEXT: `The total changed and is now`,
    TOTAL_CHANGED_TRAILING_TEXT: `. Do you want to continue with the order?`,
    TOTAL_PAID: `Total Paid`,
    TOTAL_REMAINING: `Total Remaining`,
    TOTAL_SUBSCRIPT: ``,
    TRY_AGAIN: `Try Again`,
    UNABLE_TO_FETCH_LOCATION_EXPLANATION: `It looks we we were unable to fetch your location. Please enter your address.`,
    UNAVAILABLE_ITEM: `Sorry, this item is no longer available.`,
    UNIT_GRAM: `{{$1: amount}}g`,
    UNIT_NUTRITION_INFO_CALORIES: `{{$1: amount}} Cals`,
    UNIT_NUTRITION_INFO_PROTEIN: `{{$1: amount}} Protein`,
    UNIT_NUTRITION_INFO_CARBS: `{{$1: amount}} Carbs`,
    UNIT_NUTRITION_INFO_FAT: `{{$1: amount}} Fat`,
    UNIT_NUTRITION_INFO_SUGAR: `{{$1: amount}} Sugar`,
    UP_TO_X: `up to {{$1: number}}`,
    USE_COMP_CARD: `Use Comp Card`,
    USE_COUPON: `Use Coupon`,
    USE_EBT_CARD: `EBT Card`,
    USE_EBT_CASH_BENEFIT: `EBT Cash Benefit`,
    USE_EBT_FOOD_STAMP: `EBT Food Stamp`,
    USE_GIFT_CARD: `Use Gift Card`,
    USERNAME: `Username`,
    VALIDATION_MESSAGE_EMAIL_IS_INVALID: `Email is invalid`,
    VALIDATION_MESSAGE_FIELD_IS_REQUIRED: `{{$1: field name, e.g. 'email'}} is required`,
    VALIDATION_MESSAGE_FIELD_IS_REQUIRED_GENERIC: `This field is required`,
    VALIDATION_MESSAGE_ONLY_LETTERS_AND_NUMBERS: `Please only use letters and numbers`,
    VALIDATION_MESSAGE_ONLY_LETTERS_NUMBERS_AND_SPECIAL_CHARS: `Please only use letters, numbers, &, #, or .`,
    VALIDATION_MESSAGE_PASSWORD_REQUIREMENTS: `Password must contain at least 1 digit, 1 uppercase letter, 1 lowercase letter, and be at least 14 characters long.`,
    VALIDATION_MESSAGE_PHONE_NUMBER_IS_INVALID: `Phone number is invalid`,
    VALIDATION_MESSAGE_POSTAL_CODE_IS_INVALID: `Zip Code should be numeric`,
    VENDOR_UNAVAILABLE_MESSAGE: `This vendor is currently closed. Please check working hours and come back another time.`,
    VEHICLE_COLOR: `Vehicle Color`,
    VEHICLE_MAKE: `Vehicle Make`,
    VEHICLE_MODEL: `Vehicle Model`,
    VERIFY_ACCOUNT: `Verify Account`,
    VERIFY_ACCOUNT_CTA: `Verify your account to begin placing orders.`,
    VIDEO_LOAD_FAILED: `Video failed to load`,
    VIEW: `View`,
    WE_APOLOGIZE: `We apologize for any inconvenience.`,
    WEIGHING_INSTRUCTIONS: `Place your item on the scale`,
    WELCOME_BACK: `Welcome back! Items picked just for you:`,
    WELCOME_BACK_WITH_NAME: `Welcome back, {{$1: guest name}}! Items picked just for you:`,
    WELCOME_TO: `Welcome to {{$1: org name}}!`,
    WELCOME_TO_NO_NAME: ``,
    WHEN_WOULD_YOU_LIKE_YOUR_ORDER: `Choose Order Time`,
    WHICH_OUTPOST_FOR_YOUR_ORDER: `Where would you like your order?`,
    YES: `Yes`,
    YOU_PREVIOUSLY_ORDERED: `You previously ordered`,
    YOUR_CURRENT_LOCATION: `Your current location`,
    YOUR_NAME: `Your Name`,
    YOUR_ORDER: `Your Order`,
    YOUR_SUBTOTAL: `Your Subtotal`,
    ZOOM_TOGGLE_ON: `Tap to enable Pinch to Zoom`,
    ZOOM_TOGGLE_OFF: `Tap to disable Pinch to Zoom`,
    ZOOM_INSTRUCTIONS: `Pinch to Zoom is enabled. Use two fingers to zoom in and out.`,
    STARBUCKS_COVER_1_BUTTON: `I Know What I Want`,
    STARBUCKS_COVER_2_BUTTON: `Show Me Around`,
    STARBUCKS_COVER_2_TITLE: `Experiences, Tastings, Learning`,
    STARBUCKS_COVER_PROMO_A: `Coffee Finder`,
    STARBUCKS_COVER_PROMO_B: `Starbucks Rewards`,
    STARBUCKS_EXPERIENCES_TITLE: `Experiences`,
    STARBUCKS_EXPERIENCES_SUBTITLE: `Immerse yourself in the craft, theater and innovation of coffee when you book a Signature Experience`,
    STARBUCKS_EXPERIENCES_RESERVE_NOW: `Reserve for Today`,
    STARBUCKS_EXPERIENCES_RESERVE_LATER: `Reserve for Later`,
    STARBUCKS_EXPERIENCES_BACK: `Back to Experiences`,
    STARBUCKS_WAYFINDER_CARD_EXPERIENCE_BUTTON: `Learn More`,
    STARBUCKS_WAYFINDER_CARD_MENU_BUTTON: `Go to Menu`,
    STARBUCKS_WAYFINDER_KIOSK_LABEL_FIND: `Where am I?`,
    STARBUCKS_WAYFINDER_KIOSK_LABEL: `You Are Here`,
    STARBUCKS_WAYFINDER_BATHROOMS_LABEL: `Bathrooms`,
    STARBUCKS_WAYFINDER_OPEN_SEATING_LABEL: `Open Seating`,
    STARBUCKS_WAYFINDER_HOTSPOTS_LABEL: `Find a venue`,
    STARBUCKS_WAYFINDER_DIRECTIONS_TITLE: `Drag with your finger to explore the space.`,
    STARBUCKS_WAYFINDER_DIRECTIONS_SUBTITLE: `Tap a highlighted venue to learn more about it.`,
    STARBUCKS_COFFEE_FINDER_QUESTION_1_TITLE: `Adventurousness`,
    STARBUCKS_COFFEE_FINDER_QUESTION_1_SUBTITLE: `How adventurous do you like your coffee?`,
    STARBUCKS_COFFEE_FINDER_QUESTION_2_TITLE: `Preferred flavors`,
    STARBUCKS_COFFEE_FINDER_QUESTION_2_SUBTITLE: `When you think about your coffee, which flavors sound the most delicious to you?`,
    STARBUCKS_COFFEE_FINDER_QUESTION_3_TITLE: `Strength`,
    STARBUCKS_COFFEE_FINDER_QUESTION_3_SUBTITLE: `How strong do you enjoy your coffee?`,
    STARBUCKS_COFFEE_FINDER_QUESTION_4_TITLE: `Preferred coffee Order`,
    STARBUCKS_COFFEE_FINDER_QUESTION_4_SUBTITLE: `How do you usually like to order your coffee?`,
    STARBUCKS_COFFEE_FINDER_QUESTION_EDIT: `Tap to edit`,
    STARBUCKS_COFFEE_FINDER_QUESTION_CONFIRM: `Confirm your selection`,
    STARBUCKS_COFFEE_FINDER_QUESTION_RESTART: `Start over`,
    STARBUCKS_COFFEE_FINDER_ANSWER_TITLE: `It looks like you would enjoy the`,
    STARBUCKS_COFFEE_FINDER_ANSWER_SUBTITLE: `Buy just the beans, or we recommend the drink below with this bean:`,
    STARBUCKS_COFFEE_FINDER_ANSWER_GO_TO_MENU: `View full menu`,
    STARBUCKS_COFFEE_FINDER_ANSWER_RESTART: `Start over`,
    STARBUCKS_VIEW_WAYFINDER: `Show me around`,
    STARBUCKS_VIEW_COFFEE_FINDER: `Narrow my options`,
    STARBUCKS_VIEW_EXPERIENCES: `View Experiences`,
    // v2 string keys (semantic instead of specific)
    'cover.session.timeout': `Something went wrong. Please try again.`,
    'cover.reduced-height.button': `Start Ordering`,
    'cover.loyalty.title': `Welcome`,
    'cover.five-guys.documents': `Five Guys food information`,
    'cover.five-guys.allergen-nutrition': `Allergen & Nutritional Info`,
    'cover.five-guys.ingredients': `Ingredients`,
    'cover.five-guys.storm-document-instructions': `Press left for allergen and nutritional information. Press right for ingredients.`,
    'cover.loyalty.login': `Login with Loyalty`,
    'cover.loyalty.guest': `Start as Guest`,
    'guest-identifier.button.quit': `Quit`,
    'guest-identifier.button.skip': `Skip`,
    'guest-identifier.button.back': `Back`,
    'guest-identifier.button.next': `Continue`,
    'guest-identifier.button.submit.cover': `Continue`,
    'guest-identifier.button.submit.edit': `Continue`,
    'guest-identifier.button.submit.checkout': `Continue`,
    'guest-identifier.phone.tos.button-back': `Back`,
    'guest-identifier.phone.tos.title': `Terms of Service`,
    'guest-identifier.phone.tos.link': `Phone Number Terms of Service`,
    'guest-identifier.phone.title': ``,
    'guest-identifier.table.instructions.table-number': ``,
    'guest-identifier.table.instructions.tracker-number': ``,
    'guest-identifier.name.loyalty-title': `Sign in with your loyalty account`,
    'guest-identifier.name.loyalty-button': `Sign in with Loyalty`,
    'guest-identifier.name.loyalty-separator': `or`,
    'guest-identifier.name.loyalty-error-title': `Oops! No name found`,
    'guest-identifier.name.loyalty-error-description': `Sorry, we couldn’t find an associated name for the account. Please enter your name below`,
    'inactivity-timer.button.continue': `Yes, I need more time`,
    'inactivity-timer.button.exit': `No, I'm done`,
    'item-card.add.with-quantity': `Add ({{$1: quantity}})`,
    'image.scan.default.alt': `Scan your barcode or QR code`,
    'loyalty.view_coupons': `View Coupons`,
    'loyalty.add_coupons': `Add Coupons`,
    'loyalty.label.manual_redemption': `Redemption Code`,
    'loyalty.label.manual_redemption_instructions': `Enter your redemption code to redeem your reward`,
    'loyalty.label.welcome.new-user': `Hello, {{$1: user}}! You now have a loyalty account`,
    'loyalty.reward_label.payment_unapplied': `Apply payment to card on file`,
    'loyalty.reward_label.redeemable_unredeemed': `Redeem using {{$1: points}} points`,
    'loyalty.reward_label.payment_at_checkout': `Payment can be applied at Checkout`,
    'loyalty.reward_group_label.payment': `Payment Rewards`,
    'loyalty.rewards.payment-reward.name': `Pay Using Balance`,
    'loyalty.rewards.payment-reward.name-with-amount': `Pay Using Balance ({{$1: amount}})`,
    'loyalty.rewards.payment-reward.description': `Your current balance`,
    'loyalty.rewards.card-on-file-payment.description': `Payment will be charged to your card on file.`,
    'loyalty.redemption_auth.back_to_rewards': `Back to Rewards`,
    'loyalty.redemption_auth.subtitle': `Scan your redemption barcode to redeem your reward`,
    'loyalty.redemption_auth.submit': `Redeem Reward`,
    'loyalty.signup.header': ``,
    'loyalty.signup.email-already-exists': `An account already uses this email address. Use a different email or sign in.`,
    'loyalty.signup.email-and-sms-consent': `I consent to receiving marketing materials by SMS and email.`,
    'loyalty.signup.enter-your-email-address': `Enter your email address`,
    'loyalty.signup.enter-your-name': `Enter your name`,
    'loyalty.signup.enter-your-phone-number': `(555) 555-5555`,
    'loyalty.signup.consent-required': `Please accept the terms and conditions to continue.`,
    'loyalty.checkout-signup.header': `Last chance to sign up and earn rewards on this order!`,
    'loyalty.checkout-signup.skip': `Skip to checkout`,
    'loyalty.checkout-signup.success.new-account.title': `New Account Created`,
    'loyalty.checkout-signup.success.new-account.description': `Thanks for joining, you will earn rewards on this order!`,
    'loyalty.checkout-signup.success.existing-account.title': `Account Already Exists`,
    'loyalty.checkout-signup.success.existing-account.description': `You've previously signed up with this number. You're now logged in.`,
    'loyalty.checkout-signup.error.title': `Error creating account`,
    'loyalty.checkout-signup.error.description': `Something went wrong during account creation`,
    'loyalty.checkout-signup.error.skip': `Skip to checkout`,
    'loyalty.checkout-signup.error.try-again': `Try again`,
    'menu.item.reset': `Reset Item`,
    'menu.mod-group.quick-select': `Quick Select`,
    'menu.mod-group.quick-select-active': `Selected`,
    'menu.mod-group.quick-select-clear': `Tap to Deselect`,
    'menu.page.scanner.initial.title': `Scan Items to Begin Ordering`,
    'menu.page.scanner.scanned.title': `Cart Items`,
    'menu.scanned-item-added-dialog.button.go-to-scanner-page': `View Scanned Items`,
    'order-history.button.add-to-order': `Add available items ({{$1: number}})`,
    'order-history.title': `Order History`,
    'order-history.subtitle': `All your previous orders will appear here. Interact with items to view or add them to your current order. You can add all items from an order from a single tap.`,
    'order-history.next-page': `Next`,
    'order-history.previous-page': `Previous`,
    'order-history.screen.close': `Close`,
    'order-history.screen.error.title': `Something went wrong`,
    'order-history.screen.error.subtitle': `Failed to load order history - please try again later.`,
    'order-history.screen.error.button.back': `Back`,
    'order-history.screen.error.button.retry': `Retry`,
    'order-history.screen.no-orders': `No previous orders found. When you place an order, come back here to see it.`,
    'order-history.screen.order-has-quick-add-uneligible-items': `Some items in this order aren’t available for quick reorder. Please add the items by tapping on them individually.`,
    'order-history.section.button.view-all': `See More of My Orders`,
    'order-history.topbar.button.back': `Back`,
    'loyalty.unapplicable_rewards_warning_title': `Unapplicable Rewards`,
    'loyalty.unapplicable_rewards_warning_description': `To redeem the selected reward, please add the applicable items to your cart. The reward and discount will be applied at checkout.`,
    'loyalty.unapplicable_rewards_warning_confirm': `Apply`,
    'loyalty.discard_reward_changes.title': `Discard Reward Changes?`,
    'loyalty.discard_reward_changes.description': `You have reward changes that have not been applied. Do you want to discard your changes?`,
    'loyalty.discard_reward_changes.keep_editing': `Keep Editing`,
    'loyalty.discard_reward_changes.discard': `Discard Changes`,
    'loyalty.rewards.terms_button_label': `Tap for details`,
    'loyalty.rewards.terms_dialog.apply': `Apply Reward`,
    'loyalty.rewards.terms_dialog.remove': `Remove Reward`,
    'loyalty.rewards.terms_dialog.back': `Back`,
    'loyalty.reward_ineligible.title': `Your "{{$1: reward name}}" reward isn't eligible yet`,
    'loyalty.reward_ineligible.title_generic': `Your reward isn't eligible yet`,
    'loyalty.reward_ineligible.title_plural': `{{$1: count}} rewards aren't eligible yet`,
    'loyalty.reward_ineligible.description': `Your order doesn't qualify for this reward yet. Go back to the menu to update your order, or continue without it.`,
    'loyalty.reward_ineligible.description_plural': `Your order doesn't qualify for these rewards yet. Go back to the menu to update your order, or continue without them.`,
    'loyalty.reward_ineligible.back_to_menu': `Go back to menu`,
    'loyalty.reward_ineligible.continue': `Continue without it`,
    'loyalty.reward_ineligible.continue_plural': `Continue without them`,
    'loyalty.auth_method.manual_redemption': `Manual Entry`,
    'loyalty.button.menu.authed': `Rewards`,
    'loyalty.button.menu.unauthed': `Rewards`,
    'loyalty.button.checkout.authed': `Rewards`,
    'loyalty.button.checkout.unauthed': `Rewards`,
    'loyalty.error.reward_failed_to_apply': `Failed to apply reward. Please try again.`,
    'loyalty.login.pin.label': `Enter Your PIN`,
    'loyalty.login.pin.error-required': `Please enter a PIN.`,
    'loyalty.login-options.button.skip.menu': `Skip and Continue to Menu`,
    'loyalty.login-options.button.skip.checkout': `Skip and Continue to Payment`,
    'loyalty.login-options.button.skip.dining-option-collection': `Skip and Continue Checkout`,
    'loyalty.login-options.title': `Choose an option to log in or continue as a guest`,
    'loyalty.login-options.subtitle': `You can always log in later`,
    'loyalty.login-options.signup.link': `No account? Sign up`,
    'loyalty.login-options.barcode.title': `Scan Barcode`,
    'loyalty.login-options.barcode.subtitle': `Scan your barcode to log in`,
    'loyalty.login-options.email-phone-number.title': `Email and Phone Number`,
    'loyalty.login-options.email-phone-number.subtitle': `Enter your email and phone number to log in`,
    'loyalty.login-options.email-password.title': `Email and Password`,
    'loyalty.login-options.email-password.subtitle': `Enter your email and password to log in`,
    'loyalty.login-options.username-password.title': `Username and Password`,
    'loyalty.login-options.username-password.subtitle': `Enter your username and password to log in`,
    'loyalty.login-options.phone-number.placeholder': `(555) 555-5555`,
    'loyalty.login-options.phone-number.title': `Phone Number`,
    'loyalty.login-options.phone-number.subtitle': `Enter your phone number to log in`,
    'loyalty.login-options.card-number.title': `Card Number`,
    'loyalty.login-options.card-number.subtitle': `Enter your card number to log in`,
    'loyalty.login-options.email.title': `Email`,
    'loyalty.login-options.email.subtitle': `Enter your email to log in`,
    'loyalty.login-options.or': `OR`,
    'loyalty.login-options.popup.close': `Close`,
    'loyalty.error.or': `OR`,
    'loyalty.login-error.saved-form': `Your submitted information:`,
    'loyalty.login-error.try-again.button': `Try Again`,
    'loyalty.otp.enter-code.title': `Enter the code we sent you`,
    'loyalty.otp.sent-to.email': `We emailed a code to {{$1: masked email}}`,
    'loyalty.otp.sent-to.sms': `We texted a code to {{$1: masked phone number}}`,
    'loyalty.otp.verify': `Verify`,
    'loyalty.otp.resend': `Resend code`,
    'loyalty.otp.code.error.invalid': `That code is incorrect. Please try again.`,
    'loyalty.otp.send-error.message': `We couldn't match exactly one account for that information. Please check it and try again.`,
    'loyalty.login.fallback.phone-number.button': `Use Phone Number Instead`,
    'loyalty.login.fallback.barcode.button': `Scan a Barcode or QR Code Instead`,
    'loyalty.login.fallback.card-number.button': `Use Card Number Instead`,
    'loyalty.login.fallback.email.button': `Use Email Instead`,
    'loyalty.login.fallback.email-phone-number.button': `Use Email and Phone Number Instead`,
    'loyalty.login.fallback.email-password.button': `Use Email and Password Instead`,
    'loyalty.login.fallback.username-password.button': `Use Username and Password Instead`,
    'loyalty.login.auth-method.phone-number.title': `Phone Number`,
    'loyalty.login.auth-method.barcode.title': `Scan Barcode or QR Code`,
    'loyalty.login.auth-method.card-number.title': `Card Number`,
    'loyalty.login.auth-method.email.title': `Email`,
    'loyalty.login.auth-method.email-phone-number.title': `Email and Phone Number`,
    'loyalty.login.auth-method.email-password.title': `Email and Password`,
    'loyalty.login.auth-method.username-password.title': `Username and Password`,
    'vitrine.error.order_validation_failed': `Failed to validate order. Please try again.`,
    'vitrine.error.handshake_failed': `Could not connect to the kiosk. Please contact a staff member for help.`,
    'vitrine.error.request_timeout': `Could not complete the request. Please try again.`,
    'vitrine.error.request_aborted': `Could not complete the request. Please try again.`,
    'vitrine.error.analytics_send_error': `Could not send analytics. Please try again.`,
    'vitrine.error.garcon_incompatible_build': `Could not connect to the kiosk. Please contact a staff member for help.`,
    'vitrine.error.loyalty-login-no-matching-user-title': `Your account could not be found`,
    'vitrine.error.loyalty-login-no-matching-user-message': `We couldn't find a user with the information you provided.`,
    'vitrine.error.loyalty_not_enough_points': `Your account does not have enough points to redeem the selected rewards.`,
    'vitrine.error.order_total_above_max': `The order total has exceeded the maximum allowed. Please contact a staff member for help.`,
    'vitrine.error.empty_menu': `The menu you selected is currently empty. Please try a different dining option.`,
    'maitred.error.integration_bad_config': `There seems to be an issue with the configuration of the kiosk. Please contact a staff member for help.`,
    'checkout.done.expo_receipt.error': `Could not print receipt. Please try again.`,
    'checkout.done.expo_receipt.success': `Receipt sent! Please ask a team member for assistance.`,
    'checkout.done.print-receipt.out-of-paper': `The printer is out of paper. Please see a team member for your receipt.`,
    'refunder.viewing-results.button.cancel': `Cancel`,
    'refunder.viewing-results.button.refund-all': `Refund All`,
    'refunder.viewing-results.button.view-transaction': `View Transactions`,
    'refunder.viewing-results.button.done': `Done`,
    'refunder.viewing-results.button.back': `Back`,
    'refunder.viewing-results.button.search': `Search for Order`,
    'refunder.viewing-results.refund-order.error-print-receipt': `The order was refunded successfully, but we were unable to print the receipt. Please try printing again.`,
    'refunder.viewing-results.refund-order.success': `The order was refunded successfully.`,
    'refunder.viewing-results.refund-order.error': `Failed to refund order. Please try again.`,
    'refunder.viewing-results.order.refunded': `Order Refunded`,
    'refunder.viewing-results.order.total': `Order Total`,
    'refunder.search-form.button.back': `Back`,
    'refunder.search-form.button.search': `Search`,
    'refunder.viewing-order.title': `All Transactions for Order #{{$1: order number}}`,
    'refunder.viewing-order.subtitle': `Please select the transaction you would like to refund.`,
    'refunder.viewing-order.button.cancel': `Cancel`,
    'refunder.viewing-order.button.refund': `Refund`,
    'refunder.viewing-order.button.refund-partial': `Refund Partial`,
    'refunder.viewing-order.button.retry': `Retry`,
    'refunder.viewing-order.button.done': `Done`,
    'refunder.viewing-order.button.back': `Back`,
    'refunder.viewing-order.refund-transaction.error': `Failed to refund transaction. Please try again.`,
    'refunder.viewing-order.refund-transaction.success': `Transaction refunded successfully.`,
    'refunder.viewing-order.refund-transaction.error-print-receipt': `The transaction was refunded successfully, but we were unable to print the receipt. Please try printing again.`,
    'refunder.viewing-order.transaction.total': `Total`,
    'refunder.viewing-order.transaction.refunded': `Refunded`,
    'refunder.viewing-order.error.title': `Error`,
    'refunder.viewing-order.error.message': `Failed to load order. Please try again.`,
    'refunder.viewing-order.error.button.retry': `Retry`,
    'refunder.viewing-order.error.button.back': `Back`,
    'refunder.partial-transaction-refund.error.amount-too-high': `The amount is too high.`,
    'refunder.partial-transaction-refund.error.amount-too-low': `The amount is too low.`,
    'refunder.partial-transaction-refund.error.amount-invalid': `The amount is invalid.`,
    'refunder.partial-transaction-refund.error.amount-required': `The amount is required.`,
    'refunder.partial-transaction-refund.error': `There was an issue processing the refund. Please try again.`,
    'refunder.partial-transaction-refund.button.submit': `Submit Partial Refund`,
    'refunder.partial-transaction-refund.button.back': `Back`,
    'refunder.partial-transaction-refund.title': `Partially Refund Transaction`,
    'refunder.partial-transaction-refund.hint': `Please enter the amount you would like to refund. Maximum amount is {{$1: amount}}`,
    'refunder.partial-transaction-refund.label': `Amount`,
    'refunder.partial-transaction-refund.placeholder': `0.00`,
    'checkout.cart.alcohol-verification.age-requirement': `21+`,
    'checkout.cart.alcohol-requires-food.notice': `Alcohol can only be ordered with a food item.`,
    'checkout.cart.alcohol-requires-food.title': `Add a food item`,
    'checkout.cart.alcohol-requires-food.description': `Alcohol can only be ordered with food. Add a food item to your order, or remove the alcohol to check out.`,
    'checkout.cart.group-ordering.actions.change-recipient': `Edit Recipient`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.title': `Assign Item to`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.cancel': `Cancel`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.add-new': `Add New`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.reassign': `Reassign`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.who-is-this-for': `Who is this item for?`,
    'checkout.cart.guest-ordering.recipient-assignment-dialog.assign-to-guest': `Assign to this Guest`,
    'checkout.cart.starbucks-stars': `{{$1: amount}} \u2605 item`,
    'checkout.cart.mod-group-heading': `{{$1: mod group name}}:`,
    'checkout.cart.upsize.title': `Make it {{$1: size name}}?`,
    'checkout.cart.upsize.label': `Tap here to make the {{$1: ordered item name}} {{$2: size name}}.`,
    'menu.nav.starbucks.menu': `Main Menu`,
    'menu.nav.starbucks.experiences': `Experiences`,
    'menu.nav.starbucks.wayfinder': `Wayfinder`,
    'menu.secret-menu.access-code.title': `Enter Access Code`,
    'menu.secret-menu.access-code.button.back': `Back`,
    'menu.secret-menu.access-code.button.submit': `Submit`,
    'menu.secret-menu.access-code.error': `Invalid access code`,
    'menu.secret-menu.access-code.success': `Access code accepted`,
    'menu.toast.added-to-cart': `Added to Cart`,
    'menu.toast.combo-savings': `Combo Savings!`,
    'menu.toast.discount-applied': `Discount Applied!`,
    'menu.exit-verification.title': `Manager Verification Required`,
    'menu.exit-verification.description': `An employee access code is required to exit. Please ask a team member for assistance.`,
    'menu-item.starbucks-stars': `{{$1: amount}} \u2605`,
    'menu-item.product-information.title': `Product Information`,
    'menu-item.product-information.open': `View product information`,
    'menu-item.product-information.tab.product-info': `Product Info`,
    'menu-item.product-information.tab.nutrition': `Nutrition`,
    'menu-item.product-information.tab.allergens': `Allergens`,
    'menu-item.product-information.nutrition.option': `Option {{$1: number}}`,
    'menu-item.product-information.empty': `Information is not available.`,
    'menu-item.scale.title': `Please place your item on the scale.`,
    'menu-item.scale.weight': `{{$1: weight}} {{$2: unit}}`,
    'menu-item.scale.weighs': `Weighs`,
    'menu-item.scale.price-per-unit': `{{$1: price}} per {{$2: unit}}`,
    'menu-item.scale.price': `Price`,
    'menu-item.scale.instructions': `Use the scale to weigh your item. To reset, tap the button indicated on the scale.`,
    'menu-item.scale.weight-per-unit': `{{$1: weight}} {{$2: unit}}`,
    'menu-item.scale.reading-weight': `Weighing...`,
    'menu-item.scale.error-reading-weight': `Error reading weight. Please try again.`,
    'menu-item.scale.error-no-weight': `Please weigh your item if you wish to add it to your order.`,
    'menu-item.scale.no-scale': `No scale detected. Please contact a staff member for help.`,
    'also-consider.title': `Also consider...`,
    'also-consider.back-to-item': `Back to Item`,
    'also-consider.add-to-order': `Add to Order`,
    'also-consider.skip-add-on': `Skip Add-On`,
    'menu-item.assorted.title': `Build Your Assortment`,
    'menu-item.assorted.count': `{{$1: count}} of {{$2: total}} Selected`,
    'menu-item.row-size-mod.option.none': `None`,
    'menu-item.upsize.prompt.title': `Would you like a bigger size?`,
    'menu-item.upsize.comparison.title': `Upgrade Size?`,
    'menu-item.upsize.comparison.accept': `Yes, Upgrade`,
    'menu-item.upsize.comparison.speech': `Upgrade from {{$1: current choice}} to {{$2: target choice}}?`,
    'menu-item.upsize.comparison.price-difference': `Price difference: {{$1: price difference}}.`,
    'menu-item.upsize.prompt.accept': `Yes, make it {{$1: size name}}`,
    'menu-item.upsize.prompt.decline': `No, thanks`,
    'menu-item.upsize.nudge': `Make it {{$1: size name}}`,
    'menu-item.upsize.selection-loss.title': `Keep your customizations?`,
    'menu-item.upsize.selection-loss.description': `Some of your choices aren't available in {{$1: size name}} and will be reset to the standard options.`,
    'menu-item.upsize.selection-loss.confirm': `Continue with {{$1: size name}}`,
    'menu-item.upsize.selection-loss.cancel': `Keep my choices`,
    'menu-item.high-sugar-warning.description': `Warning: This icon indicates that the added sugars content of this item is equal to or higher than the total daily recommended limit of added sugars for a 2,000 calorie diet (50 g). Eating or drinking high amounts of added sugars over time can increase risk of type 2 diabetes, weight gain and tooth decay.`,
    'menu-item.high-sodium-warning.description': `Warning: This icon indicates that the sodium (salt) content of this item is higher than the total daily recommended limit (2,300 mg). High sodium intake can increase blood pressure and risk of heart disease and stroke.`,
    'menu-item.group-ordering.items-for': `Group Ordering - Items For:`,
    'menu-item.group-ordering.prompt.title': `Ordering with a Group?`,
    'menu-item.group-ordering.prompt.description': `The group ordering option will ask you which member of your party each item is for. Tap "Yes" to enable group ordering. Tap "No" if you'd like to continue with this option.`,
    'menu-item.group-ordering.prompt.button.accept': `Yes, Group Order`,
    'menu-item.group-ordering.prompt.button.decline': `No, Thanks`,
    'menu-item.group-ordering.add-recipient': `Add New`,
    'menu-item.group-ordering.add-recipient-name.dialog.title': `Add New Recipient`,
    'menu-item.group-ordering.add-recipient-name.dialog.placeholder': `Enter the name of the recipient`,
    'menu-item.group-ordering.add-recipient-name.dialog.button.submit': `Assign To Guest`,
    'menu-item.group-ordering.add-recipient-name.dialog.button.cancel': `Cancel`,
    'menu-item.group-ordering.recipient-picker.title': `Who is this for?`,
    'menu-item.group-ordering.recipient-picker.add-new': `Add New`,
    'menu-item.nested-mod-groups.edit': `Edit`,
    'menu-item.nested-mod-groups.popup.done': `Done`,
    'menu-item.nested-mod-groups.popup.cancel': `Cancel`,
    'menu-item.nested-mod-groups.popup.back': `Back`,
    'menu-item.nested-mod-groups.popup.next': `Next`,
    'checkout.cart.remove-item-verification.title': `Manager Verification Required`,
    'checkout.cart.remove-item-verification.description': `An employee access code is required to remove this item from the cart. Please ask a team member for assistance.`,
    'checkout.cart.confirm-remove-item.title': `Remove item?`,
    'checkout.cart.confirm-remove-item.description': `Are you sure you want to remove {{$1: item name}} from your cart?`,
    'checkout.cart.confirm-remove-item.cancel': `Cancel`,
    'checkout.cart.confirm-remove-item.confirm': `Remove`,
    'checkout.cart.pre-checkout-recommendations.title': `Complete your order`,
    'checkout.cart.pre-checkout-recommendations.continue-button': `Continue`,
    'checkout.cart.pre-checkout-recommendations.back-button': `Back`,
    'checkout.cart.item-86d.title.items': `Some items are no longer available`,
    'checkout.cart.item-86d.title.mods': `Some modifications are no longer available`,
    'checkout.cart.item-86d.title.mixed': `Some items and modifications are no longer available`,
    'checkout.cart.item-86d.description.items': `The following items have been removed from your cart:`,
    'checkout.cart.item-86d.description.mods': `The following modifications have been removed from your items:`,
    'checkout.cart.item-86d.description.mixed': `The following are no longer available and have been removed:`,
    'checkout.cart.item-86d.entity.menu-item': `{{$1: item name}}`,
    'checkout.cart.item-86d.entity.price-option': `{{$1: item name}} — {{$2: price option name}}`,
    'checkout.cart.item-86d.entity.mod': `{{$1: mod name}} (in {{$2: item name}})`,
    'checkout.cart.item-86d.edit-item-button': `Edit`,
    'checkout.payment-options.tip.invalid-tip-amount': `Sorry, you cannot apply a tip larger than the order total. Please enter a tip value that is less than the order subtotal minus any discounts.`,
    'checkout.payment-options.name.title': `Hi, {{$1: name}}`,
    'checkout.payment-options.gift-cards.prompt.title': `Select a Gift Card Method`,
    'checkout.payment-options.gift-cards.prompt.default-option': `Gift Card`,
    'checkout.payment-options.pay-with-cash-machine.label': `Tap to Pay with Cash Machine`,
    'checkout.payment-options.pay-with-cash-machine.button': `Cash`,
    'checkout.payment-options.donation.or': `OR`,
    'checkout.payment-options.service-charge.info-message': ``,
    'checkout.payment-options.service-charge.info-label': `Service charge information`,
    // when a user has no outstanding balance, can happen if they have a coupon or reward applied
    // and remove a previously applied item (no auto-close). See BITE-15614
    'checkout.payment-options.credit-card.button.zero-balance': `Place Order`,
    'checkout.payment-options.inline-loyalty.view-all': `View All Rewards`,
    'checkout.payment-options.inline-loyalty.apply': `Apply Rewards`,
    'checkout.payment-options.inline-loyalty.greeting': `Hi, {{$1: name}}!`,
    'checkout.payment-options.inline-loyalty.star-balance': `Star Balance: {{$1: stars}}\u2605`,
    'checkout.payment-options.inline-loyalty.celebration.title': `Happy Birthday!`,
    'checkout.payment-options.inline-loyalty.celebration.title-with-name': `Happy Birthday, {{$1: name}}!`,
    'checkout.payment-options.inline-loyalty.celebration.subtitle': `Enjoy your treat!`,
    'checkout.payment-options.inline-loyalty.error.title': `Unable to Apply Rewards`,
    'checkout.payment-options.inline-loyalty.error.message': `There was an error applying your rewards. Please try again.`,
    'loyalty.starbucks.header.redeemable-stars': `Redeemable Stars`,
    'loyalty.starbucks.header.stars-expiring': `{{$1: stars}}\u2605 expiring {{$2: date}}`,
    'loyalty.starbucks.header.stars-no-expiry': `Your Stars won't expire`,
    'loyalty.starbucks.progress.next-status-by-date': `Earn {{$1: stars}}\u2605 by {{$2: date}} to reach {{$3: level}} Status`,
    'loyalty.starbucks.progress.next-status': `Earn {{$1: stars}}\u2605 to reach {{$2: level}} Status`,
    'loyalty.starbucks.progress.maintain-status-by-date': `Earn {{$1: stars}}\u2605 by {{$2: date}} to extend your {{$3: level}} status for another year`,
    'loyalty.starbucks.progress.maintain-status': `Earn {{$1: stars}}\u2605 to extend your {{$2: level}} status for another year`,
    'loyalty.starbucks.progress.extended': `Extended for another year`,
    'loyalty.starbucks.progress.annual-earned': `Annual earned`,
    'loyalty.starbucks.progress.status-target': `{{$1: level}} status`,
    'loyalty.starbucks.progress.to-extend': `to extend`,
    'loyalty.starbucks.birthday.title': `Happy Birthday, {{$1: name}}!`,
    'loyalty.starbucks.birthday.title-no-name': `Happy Birthday!`,
    'loyalty.starbucks.birthday.redeem': `Redeem your birthday coupon!`,
    'loyalty.starbucks.birthday.description': `Enjoy a favorite drink or treat on us. Redeem at checkout, or let your barista know when ordering.`,
    'loyalty.starbucks.birthday.valid-until': `Valid until {{$1: date}}`,
    'loyalty.starbucks.member-since': `Member since {{$1: year}}`,
    'loyalty.starbucks.member-since.subtitle': `Thank you for being a loyal Starbucks Rewards member.`,
    'checkout.paying.starbucks.title': `Please scan the QR Code on your Starbucks App now`,
    'checkout.paying.starbucks.subtitle': `Please ensure you have the Scan to Pay feature ready in your Starbucks App to complete your order.`,
    'checkout.paying.starbucks.cancel-button': `Cancel`,
    'checkout.paying.error.card-not-accepted': `Sorry, this card is not accepted here. Please try a different card.`,
    'checkout.paying.error.loyalty-payment': `Sorry, we were unable to process your payment. Please try again.`,
    'checkout.paying.error.pos.print': `Continue and Print Receipt`,
    'checkout.paying.error.table-tracker.print': `Continue and Print Receipt`,
    'checkout.paying.error.table-tracker.title': `Oops! We couldn't get your pager.`,
    'checkout.paying.error.table-tracker.instructions': `Your order went though, but there was an error in dispensing your table tracker to track your order. Proceed to the next step to receive your receipt.`,
    'checkout.paying.error.title': `Oops!`,
    'checkout.paying.sending-table-tracker-request.title': `Dispensing Pager`,
    'checkout.paying.sending-table-tracker-request.subtitle': `Please wait while we dispense your table-tracker.`,
    'checkout.paying.cash-machine.pinging.title': `Checking On Cash Machine`,
    'checkout.paying.cash-machine.pinging.subtitle': `Please wait while we check if the cash machine is available.`,
    'checkout.paying.cash-machine.awaiting.title': `Cash Machine In Use`,
    'checkout.paying.cash-machine.awaiting.subtitle': `Please wait until the cash machine is available.`,
    'checkout.paying.cash-machine.awaiting.cancel-button': `Cancel`,
    'checkout.paying.cash-machine.receiving.title': `Cash Machine Ready`,
    'checkout.paying.cash-machine.receiving.subtitle': `Please insert your payment into the cash machine.`,
    'checkout.paying.cash-machine.receiving.cancel-button': `Cancel`,
    'checkout.paying.cash-machine.receiving.cancel-refund-button': `Cancel and Refund Payment`,
    'checkout.paying.cash-machine.receiving.partial-button': `Pay the Rest Another Way`,
    'checkout.paying.cash-machine.receiving.amount': `{{$1: amount}} remaining`,
    'checkout.paying.cash-machine.receiving.take-change-prompt': `Please take all bills and coins from the cash machine.`,
    'checkout.paying.cash-machine.cancelling.title': `Cancelling Cash Machine Payment`,
    'checkout.paying.cash-machine.cancelling.subtitle': `Please take all bills and coins from the cash machine.`,
    'checkout.paying.cash-machine.cancelling.error.title': `Error Cancelling Cash Machine Payment`,
    'checkout.paying.cash-machine.cancelling.error.subtitle': `Your payment was unable to be cancelled. Please ask a team member for help.`,
    'checkout.paying.cash-machine.cancelling.error.print': `Continue and Print Receipt`,
    'checkout.paying.cash-machine.cancelling.insufficient-change.title': `Insufficient Change`,
    'checkout.paying.cash-machine.cancelling.insufficient-change.subtitle': `The cash machine was unable to give you change for your payment, so we've refunded your payment.`,
    'checkout.paying.cash-machine.cancelling.insufficient-change.try-again-button': `Try Again`,
    'checkout.paying.cash-machine.cancelling.insufficient-change.back-button': `Back`,
    'checkout.paying.cash-machine.error.message': `The Cash Machine was unable to process your payment. Please try again.`,
    'checkout.done.details.guest-name': `Name`,
    'checkout.done.details.table-number': `Table Number`,
    'checkout.done.details.tracker-number': `Tracker Number`,
    'checkout.done.details.order-number': `Order Number`,
    'checkout.done.error.table-tracker.instructions': `There was an error retrieving your Pager. Your order has been successfully processed. Please ask an employee for help.`,
    'checkout.done.qr-code.error': `Sorry, we were unable to generate a QR code for your receipt.`,
    'printer-receipt.stored-value-remaining-balance': `{{$1: label}} Remaining Balance`,
    'printer-receipt.order-ready-at': `Your order will be ready at {{$1: time}}`,
    // IMPORTANT: Keep empty string. This is a workaround for Daves Hot Chicken because their Punchh integration lives on top of QU and they can't add their own header supposedly.
    // See BITE-16285 for more details.
    'printer-receipt.punchh-barcode-header': ``,
    // Header line printed once at the top of a refund-mode receipt (email refund path).
    'printer-receipt.refund-receipt-header': `Refund Receipt`,
    'order-reconciler.error.dining-option-unavailable': `The dining option you selected is no longer available. Your order has been updated to use "{{$1: dining option name}}".`,
    'order-reconciler.error.items-unavailable': `Some items in your order are no longer available. Your order has been updated to remove the unavailable items. The following items have been removed: {{$1: item names}}`,
    'page-nav.order-history-item.title': `Your Orders`,
    'page-nav.order-history-item.cta': `View my orders`,
    'print.error.phone-number-label': `Phone Number`,
    'print.error.guest-name-label': `Name`,
    'print.error.table-number-label': `Table Number`,
    'print.error.tracker-number-label': `Tracker Number`,
    'print.error.order-number-label': `Order Number`,
    // RESERVED TEST FIXTURE — do NOT translate the Italian (it-it) value. Localization fallback
    // unit tests rely on this key being filled in en/de/es but left blank in it-it to exercise the
    // requested-language -> primary-language -> English chain. See
    // packages/localization/test/localizable_strings.test.ts and
    // packages/vitrine/server/src/universal/trpc/translations/translations.test.ts.
    'debug.untranslated-locale': `Add a Tip`,
};
// Derive CustomStringKey as an object from the keys
export const CustomStringKey = Object.fromEntries(Object.keys(Strings).map((key) => [key, key]));
//# sourceMappingURL=strings.en-us.js.map